import React, { useState, useRef, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Table,
  Checkbox,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "reactstrap"
import "./Contract.scss"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Fn_FillListData, Fn_AddEditData, Fn_DeleteData } from "store/Functions"
import { API_WEB_URLS } from "constants/constAPI"
import { getGlobalOptions } from "helpers/GlobalOptionsHelper"
import html2pdf from "html2pdf.js"
import { jsPDF } from "jspdf"
import toastr from "toastr"
import "toastr/build/toastr.css"

// Helper function to format date from ISO to yyyy-MM-dd
const formatDateForInput = dateString => {
  if (!dateString) return ""
  if (typeof dateString === "string" && dateString.includes("T")) {
    return dateString.split("T")[0]
  }
  if (dateString instanceof Date) {
    return dateString.toISOString().split("T")[0]
  }
  return dateString
}

// Helper function to convert API date format (DD-MM-YYYY) to input format (YYYY-MM-DD)
const convertApiDateToInputFormat = (apiDate) => {
  if (!apiDate) return '';
  const parts = apiDate.split('-');
  if (parts.length === 3) {
    // Check if already in YYYY-MM-DD format (first part has 4 digits)
    if (parts[0].length === 4) {
      // Already in YYYY-MM-DD format, return as is
      return apiDate;
    }
    // Convert DD-MM-YYYY to YYYY-MM-DD
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return apiDate;
}

// Helper function to convert input date format (YYYY-MM-DD) to API format (DD-MM-YYYY)
const convertInputDateToApiFormat = (inputDate) => {
  if (!inputDate) return '';
  const parts = inputDate.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert YYYY-MM-DD to DD-MM-YYYY
  }
  return inputDate;
}

// Helper function to convert date to SQL Server format (YYYY-MM-DD)
const convertDateToSQLServerFormat = (inputDate) => {
  if (!inputDate) return '';
  // If already in YYYY-MM-DD format, return as is (SQL Server compatible)
  if (typeof inputDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(inputDate)) {
    return inputDate;
  }
  // If in DD-MM-YYYY format, convert to YYYY-MM-DD
  const parts = inputDate.split('-');
  if (parts.length === 3) {
    // Check if it's DD-MM-YYYY or YYYY-MM-DD
    if (parts[0].length === 4) {
      // Already YYYY-MM-DD
      return inputDate;
    } else {
      // DD-MM-YYYY, convert to YYYY-MM-DD
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }
  // Try to parse as Date object
  try {
    const dateObj = new Date(inputDate);
    if (!isNaN(dateObj.getTime())) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    // If parsing fails, return as is
  }
  return inputDate;
}

// Helper function to format date in "DD Month YYYY" format
const formatDateForDisplay = dateString => {
  if (!dateString) return ""

  let dateObj
  if (dateString instanceof Date) {
    dateObj = dateString
  } else if (typeof dateString === "string") {
    if (dateString.includes("T")) {
      dateObj = new Date(dateString.split("T")[0])
    } else {
      dateObj = new Date(dateString)
    }
  } else {
    return ""
  }

  if (isNaN(dateObj.getTime())) return ""

  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"]

  const day = dateObj.getDate()
  const month = months[dateObj.getMonth()]
  const year = dateObj.getFullYear()

  return `${day} ${month} ${year}`
}

// Helper function to create default lifting rows
const createDefaultLiftingRows = (count = 7) => {
  return Array(count)
    .fill()
    .map(() => ({ Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" }))
}

const Contract = ({
  isModal = false,
  contractData = null,
  onClose = null,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Refs for focus management
  const contractNoRef = useRef(null)
  const contractNoSelectRef = useRef(null)
  const dateRef = useRef(null)
  const broContNoRef = useRef(null)
  const broContDateRef = useRef(null)
  const partyRef = useRef(null)
  const brokerRef = useRef(null)
  const commodityRef = useRef(null)
  const qtyRef = useRef(null)
  const unitRef = useRef(null)
  const rateRef = useRef(null)
  const importDutyRef = useRef(null)
  const tariffRef = useRef(null)
  const exchangeRateRef = useRef(null)
  const calPerTonDutyRef = useRef(null)
  const contractTypeRef = useRef(null)
  const shipMonthRef = useRef(null)
  const shipYearRef = useRef(null)
  const vesselRef = useRef(null)
  const deliveryPortRef = useRef(null)
  const shipmentFromRef = useRef(null)
  const shipmentToRef = useRef(null)
  const liftedFromRef = useRef(null)
  const liftedToRef = useRef(null)
  const remarksRef = useRef(null)
  const advPaymentRef = useRef(null)
  const pmtRef = useRef(null)
  const advDateRef = useRef(null)
  const note6Ref = useRef(null)
  const note1Ref = useRef(null)
  const note2Ref = useRef(null)
  const note3Ref = useRef(null)
  const note4Ref = useRef(null)
  const note5Ref = useRef(null)
  const saveButtonRef = useRef(null)

  const [state, setState] = useState({
    id: 0,
    Date: new Date().toISOString().split("T")[0],
    Date1: new Date().toISOString().split("T")[0],
    ContractNo: "",
    ContractId: "", // New field for dropdown selection
    originalContractNo: "", // Track original contract number for change detection
    BroContNo: "",
    BroContDate: "",
    DoDetailsArray: [],
    showSharePDFModal: false,
    pendingShareFile: null,
    AdvDate: "",
    DiffAmt: 0,
    AdvPayment: "",
    F_PartyLedger: 0,
    F_BrokerLedger: 0,
    F_ItemType: 0,
    Note5: "",
    Note6: "",
    Note1: "",
    Note2: "",
    Note3: "",
    Note4: "",
    Qty: 0,
    Rate: 0,
    Rate1: 0,
    F_UnitMaster: 0,
    F_MonthMaster: 0,
    F_YearMaster: 0,
    LiftingLedger: 0,
    Vessel: "",
    DeliveryPort: "",
    F_ContractH: "",
    ShipMentFromDate: null,
    ShipMentToDate: null,
    LiftedFromDate: null,
    LiftedToDate: null,
    F_ContractLedger: 0,
    Remarks: "",
    LorryNo: "",
    BNo: "",
    Lifted: "",
    AvgRate: 0,
    InvRate: 0,
    askForPrint: false,
    pdfPrint: false,
    pending: "",
    pmt: "",
    lifting: "",
    avgRate: "",
    BackSellerCNo: "",
    importDuty: 0,
    tariff: 0,
    exchangeRate: 0,
    calPerTonDuty: 0,
    Type: 1, // 1 for Buy, 2 for Sell
    liftingData: [],
    ContractHNo: [],
    PartyAccountArray: [],
    BrokerAccountArray: [],
    TaxAccountArray: [],
    PartyAccountArray1: [],
    BrokerAccountArray2: [],
    TaxAccountArray1: [],
    UnitArray: [],
    ItemArray: [],
    MonthArray: [],
    YearArray: [],
    GlobalArray: [],
    ContractArray: [],
    NewArray: [],
    LiftingArray: [],
    NotesArray: [],
    formData: {},
    isProgress: false,
    showSuccessMessage: false,
    showErrorMessage: false,
    message: "",
    isEditMode: false,
    // New state variables for lifting validation and calculations
    totalLiftingQty: 0,
    contractQty: 0,
    showLiftingAlert: false,
    liftingAlertMessage: "",
    DiffAmt: "0.00",
    lastContractNo: "", // Store first contract number for reference
    contractNumberChanged: false, // Track if contract number has been changed by user
    DoDetails: {}, // Object to store detailed contract information
  })

  const [liftingRows, setLiftingRows] = useState(createDefaultLiftingRows(7))

  // Modal state variables
  const [sellerModal, setSellerModal] = useState(false)
  const [brokerModal, setBrokerModal] = useState(false)
  const [doDetailsModal, setDoDetailsModal] = useState(false)
  const [editingRowIndex, setEditingRowIndex] = useState(null)
  const doNoInputRef = useRef(null)

  const [newPartyData, setNewPartyData] = useState({ Name: "", Person: "", Address: "", GSTNo: "" })
  const [newBrokerData, setNewBrokerData] = useState({ Name: "", Person: "", Address: "", GSTNo: "" })

  const [commodityModal, setCommodityModal] = useState(false)

  const [newCommodityData, setNewCommodityData] = useState({ Name: "", Alias: "" })

  const [contractModal, setContractModal] = useState(false)


  const [newContractData, setNewContractData] = useState({ Name: "" })



  const API_URL_SAVE = `${API_WEB_URLS.ContractH}/0/token`
  const API_URL_Lifting = `${API_WEB_URLS.AddLifting}/0/token`
  const API_URL = API_WEB_URLS.MASTER + "/0/token/PartyAccount"
  const API_URLBroker = API_WEB_URLS.MASTER + "/0/token/LedgerMasterBroker"
  const API_URL1 = API_WEB_URLS.MASTER + "/0/token/TaxAccount"
  const API_URL2 = API_WEB_URLS.MASTER + "/0/token/UnitMaster"
  const API_URL3 = API_WEB_URLS.MASTER + "/0/token/ItemMaster"
  const API_URL4 = API_WEB_URLS.MASTER + "/0/token/MonthMaster"
  const API_URL4Year = API_WEB_URLS.MASTER + "/0/token/YearMaster"
  // const API_URL5 = API_WEB_URLS.MASTER + "/0/token/UpdateGlobalOptions" // Now using localStorage
  const API_URL6 = API_WEB_URLS.MASTER + "/0/token/PartyAccount1"
  const API_URL7 = API_WEB_URLS.MASTER + "/0/token/ContractHNoWeb"
  const API_URL8 = API_WEB_URLS.MASTER + "/0/token/ContractHMaster"
  // const API_URL9 = API_WEB_URLS.MASTER + "/0/token/ContractNoNew"
  const API_URL10 = API_WEB_URLS.MASTER + "/0/token/LiftingMaster"
  const API_URL11 = API_WEB_URLS.MASTER + "/0/token/Notes"
  const API_URL12 = API_WEB_URLS.MASTER + "/0/token/DoDetailsByContract"
  const API_URL13 = API_WEB_URLS.MASTER + "/0/token/DoNoNew"
  const API_URL_DO_DETAILS = API_WEB_URLS.MASTER + "/0/token/DoDetails"
  const API_Delete = API_WEB_URLS.MASTER + "/0/token/DeleteContract"

  const API_SaveLedger = `${API_WEB_URLS.LedgerMaster}/0/token`
  const API_SaveCommodity = `${API_WEB_URLS.ItemMaster}/0/token`

  useEffect(() => {
    // Load master data
    Fn_FillListData(dispatch, setState, "PartyAccountArray", API_URL + "/Id/0")
    Fn_FillListData(dispatch, setState, "BrokerAccountArray", API_URLBroker + "/Id/0")
    Fn_FillListData(dispatch, setState, "TaxAccountArray", API_URL1 + "/Id/0")
    Fn_FillListData(dispatch, setState, "UnitArray", API_URL2 + "/Id/0")
    Fn_FillListData(dispatch, setState, "ItemArray", API_URL3 + "/Id/0")
    Fn_FillListData(dispatch, setState, "MonthArray", API_URL4 + "/Id/0")
    Fn_FillListData(dispatch, setState, "YearArray", API_URL4Year + "/Id/0")
    // Load GlobalOptions from localStorage instead of API
    const globalOptions = getGlobalOptions();
    setState(prevState => ({ ...prevState, GlobalArray: globalOptions }));
    Fn_FillListData(dispatch, setState, "ContractHNo", API_URL7 + "/Id/0")
    Fn_FillListData(dispatch, setState, "NotesArray", API_URL11 + "/Id/0")
    // Fn_FillListData(dispatch, setState, "NewArray", API_URL9 + "/Id/0")

    // Reset lifting data and calculations on initial load
    setLiftingRows(createDefaultLiftingRows(7))
    setState(prev => ({
      ...prev,
      LiftingArray: [],
      totalLiftingQty: 0,
      DiffAmt: "0.00",
      showLiftingAlert: false,
      liftingAlertMessage: "",
      isEditMode: false,
      originalContractNo: "", // Initialize original contract number
      contractNumberChanged: false, // Initialize contract number changed flag
    }))
  }, [])

  // Load first contract data when ContractHNo is loaded (skip when opened from modal with specific contract)
  useEffect(() => {
    if (isModal && contractData) return
    if (state.ContractHNo && state.ContractHNo.length > 0) {
      loadFirstContractData()
    }
  }, [state.ContractHNo])

  // Function to load first contract data for edit mode
  const loadFirstContractData = async () => {
    try {
      // Get the first contract from ContractHNo array
      if (state.ContractHNo && state.ContractHNo.length > 0) {
        const firstContract = state.ContractHNo[0]

        // Load the first contract's full data
        await Fn_FillListData(
          dispatch,
          setState,
          "ContractArray",
          `${API_URL8}/Id/${firstContract.Id}`
        )

        // After ContractArray is updated, populate the form with first contract data
        setTimeout(async () => {
          setState(prev => {
            const firstContractData = prev.ContractArray[0] || {}

            if (firstContractData.Id) {
              // Load contact person data for seller and buyer
              if (firstContractData.F_PartyLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray1",
                  `${API_URL6}/Id/${firstContractData.F_PartyLedger}`
                )
              }

              if (firstContractData.F_BrokerLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "BrokerAccountArray2",
                  `${API_URL6}/Id/${firstContractData.F_BrokerLedger}`
                )
              }

              // Fetch lifting data using ContractH ID
              Fn_FillListData(
                dispatch,
                setState,
                "LiftingArray",
                `${API_URL10}/Id/${firstContractData.Id}`
              )

              return {
                ...prev,
                id: firstContractData.Id || 0,
                Date: formatDateForInput(firstContractData.Date) || new Date().toISOString().split("T")[0],
                Date1: new Date().toISOString().split("T")[0],
                ContractNo: firstContractData.ContractNo || "",
                BroContNo: firstContractData.BroContNo || "",
                BroContDate: firstContractData.BroContDate || "",
                ContractId: firstContract.Id,
                originalContractNo: firstContractData.ContractNo || "",
                contractNumberChanged: false,
                F_PartyLedger: firstContractData.F_PartyLedger || 0,
                F_BrokerLedger: firstContractData.F_BrokerLedger || 0,
                F_ItemType: firstContractData.F_ItemType || 0,
                Qty: firstContractData.Qty || 0,
                Rate: firstContractData.Rate || 0,
                InvRate: firstContractData.InvRate || firstContractData.Rate || 0,
                F_UnitMaster: firstContractData.F_UnitMaster || 0,
                F_MonthMaster: firstContractData.F_MonthMaster || 0,
                F_YearMaster: firstContractData.F_YearMaster || 0,
                Vessel: firstContractData.Vessel || "",
                DeliveryPort: firstContractData.DeliveryPort || "",
                F_ContractH: "",
                ShipMentFromDate: firstContractData.ShipMentFromDate || "",
                ShipMentToDate: firstContractData.ShipMentToDate || "",
                LiftedFromDate: firstContractData.LiftedFromDate || "",
                LiftedToDate: firstContractData.LiftedToDate || "",
                F_ContractLedger: firstContractData.F_ContractLedger || 0,
                Remarks: firstContractData.Remarks || "",
                AdvPayment: firstContractData.AdvPayment || "",
                AdvDate: firstContractData.AdvDate || "",
                Note5: firstContractData.Note5 || "",
                Note6: firstContractData.Note6 || "",
                Note1: firstContractData.Note1 || "",
                Note2: firstContractData.Note2 || "",
                Note3: firstContractData.Note3 || "",
                Note4: firstContractData.Note4 || "",
                importDuty: firstContractData.importDuty || 0,
                tariff: firstContractData.tariff || 0,
                exchangeRate: firstContractData.exchangeRate || 0,
                calPerTonDuty: firstContractData.calPerTonDuty || 0,
                isEditMode: true, // Edit mode
                isEditingEnabled: false,
                contractQty: firstContractData.Qty || 0,
                BackSellerCNo: firstContractData.BackSellerCNo || "",
                SellerPerson: firstContractData.SellerPerson || "",
                BuyerPerson: firstContractData.BuyerPerson || "",
                LiftingLedger: firstContractData.LiftingLedger || "",
                Type: firstContractData.Type || 1,
                // Store first contract number for reference
                lastContractNo: firstContractData.ContractNo || "",
              }
            }
            return prev
          })
        }, 200)
      }
    } catch (error) {
      console.log("Error loading last contract data:", error)
    }
  }

  // Set ContractNo to ContractNoNew when NewArray is loaded (for new contracts)
  useEffect(() => {
    {
      setState(prev => ({
        ...prev,
        ContractNo: state.ContractNo,
        ContractId: "", // Clear ContractId for new contracts
      }))
    }
  }, [state.ContractNo])

  // Focus on ContractNo field when page loads
  useEffect(() => {
    // Focus on ContractNo field when page loads
    if (contractNoRef.current) {
      contractNoRef.current.focus()
    }
  }, [])

  // Add keyboard shortcut for Ctrl+S to save/update form
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Ctrl+S is pressed (works for both add and edit modes)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault() // Prevent default browser save behavior

        // Check if required data is loaded
        if (!state.F_PartyLedger || !state.F_BrokerLedger || !state.F_ItemType || !state.Qty || !state.Rate) {
          alert('Please wait for the form data to load completely before saving, or fill in the required fields manually.')
          return
        }

        // Check if ContractNo is filled
        if (!state.ContractNo || state.ContractNo.trim() === "") {
          alert('Please enter a contract number before saving.')
          // Focus on the contract number field
          if (contractNoRef.current) {
            contractNoRef.current.focus()
          }
          return
        }

        // Show confirmation alert with appropriate message based on mode
        const actionText = state.isEditMode ? 'update' : 'save'
        if (window.confirm(`Are you sure you want to ${actionText} this contract?`)) {
          handleSave()
        }
      }
    }

    // Add event listener
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [state.isEditMode, state.F_PartyLedger, state.F_BrokerLedger, state.F_ItemType, state.Qty, state.Rate, state.ContractNo]) // Re-run when edit mode or required fields change

  // Check for contract data passed as modal prop
  useEffect(() => {
    if (isModal && contractData) {
      console.log("Loading contract from modal props:", contractData)

      // Try to load by ID first, then by ContractNo as fallback
      let contractIdentifier =
        contractData.Id || contractData.ContractId || contractData.Contract_Id

      if (!contractIdentifier && contractData.ContractNo) {
        // If no ID found, try to load by ContractNo
        contractIdentifier = contractData.ContractNo
      }

      if (contractIdentifier) {
        // Set the contract number and trigger loading
        setState(prev => ({
          ...prev,
          ContractNo: contractIdentifier,
        }))

        // Load the contract data
        loadContractByContractNo(contractIdentifier)
      } else {
        console.error(
          "No contract identifier found in contract data:",
          contractData
        )
        toastr.error("No contract identifier found")
      }
    }
  }, [isModal, contractData])

  // Function to load contract by contract number
  const loadContractByContractNo = async contractId => {
    try {
      await Fn_FillListData(
        dispatch,
        setState,
        "ContractArray",
        `${API_URL8}/Id/${contractId}`
      )

      // After ContractArray is updated, check if we found a contract
      setTimeout(async () => {
        setState(prev => {
          const contract = prev.ContractArray[0] || {}

          // If we found a contract, load its data for editing
          if (contract.Id) {
            // Load contact person data for seller and buyer
            if (contract.F_PartyLedger) {
              Fn_FillListData(
                dispatch,
                setState,
                "PartyAccountArray1",
                `${API_URL6}/Id/${contract.F_PartyLedger}`
              )
            }

            if (contract.F_BrokerLedger) {
              Fn_FillListData(
                dispatch,
                setState,
                "BrokerAccountArray2",
                `${API_URL6}/Id/${contract.F_BrokerLedger}`
              )
            }

            // Fetch lifting data using ContractH ID
            Fn_FillListData(
              dispatch,
              setState,
              "LiftingArray",
              `${API_URL10}/Id/${contract.Id}`
            )

            // Show success message
            toastr.success(`Contract ${contractId} loaded successfully!`)

            return {
              ...prev,
              id: contract.Id || 0,
              Date:
                formatDateForInput(contract.Date) ||
                new Date().toISOString().split("T")[0],
              Date1: new Date().toISOString().split("T")[0],
              ContractNo: contract.ContractNo || contractId,
              BroContNo: contract.BroContNo || "",
              BroContDate: contract.BroContDate || "",
              ContractId: contract.Id,
              originalContractNo: contract.ContractNo || contractId,
              contractNumberChanged: false,
              F_PartyLedger: contract.F_PartyLedger || 0,
              F_BrokerLedger: contract.F_BrokerLedger || 0,
              F_ItemType: contract.F_ItemType || 0,
              Qty: contract.Qty || 0,
              Rate: contract.Rate || 0,
              InvRate: contract.InvRate || contract.Rate || 0,
              F_UnitMaster: contract.F_UnitMaster || 0,
              F_MonthMaster: contract.F_MonthMaster || 0,
              F_YearMaster: contract.F_YearMaster || 0,
              Vessel: contract.Vessel || "",
              DeliveryPort: contract.DeliveryPort || "",
              F_ContractH: "",
              ShipMentFromDate: contract.ShipMentFromDate || "",
              ShipMentToDate: contract.ShipMentToDate || "",
              LiftedFromDate: contract.LiftedFromDate || "",
              LiftedToDate: contract.LiftedToDate || "",
              F_ContractLedger: contract.F_ContractLedger || 0,
              Remarks: contract.Remarks || "",
              AdvPayment: contract.AdvPayment || "",
              AdvDate: contract.AdvDate || "",
              Note5: contract.Note5 || "",
              Note6: contract.Note6 || "",
              Note1: contract.Note1 || "",
              Note2: contract.Note2 || "",
              Note3: contract.Note3 || "",
              Note4: contract.Note4 || "",
              importDuty: contract.importDuty || 0,
              tariff: contract.tariff || 0,
              exchangeRate: contract.exchangeRate || 0,
              calPerTonDuty: contract.calPerTonDuty || 0,
              isEditMode: true, // Edit mode
              isEditingEnabled: false,
              contractQty: contract.Qty || 0,
              BackSellerCNo: contract.BackSellerCNo || "",
              SellerPerson: contract.SellerPerson || "",
              BuyerPerson: contract.BuyerPerson || "",
              LiftingLedger: contract.LiftingLedger || 0,
              Type: contract.Type || 1,
              // Store first contract number for reference
              lastContractNo: contract.ContractNo || "",
            }
          } else {
            // No existing contract found
            console.log("No contract found with contract ID:", contractId)
            toastr.error(`No contract found with contract ID: ${contractId}`)
            return {
              ...prev,
              id: 0,
              ContractArray: [],
              LiftingArray: [],
              isEditMode: false,
              isEditingEnabled: false,
              ContractNo: contractId,
            }
          }
        })
      }, 200)
    } catch (error) {
      console.error("Error loading contract:", error)
      toastr.error(`Error loading contract: ${error.message}`)
      setState(prev => ({
        ...prev,
        id: 0,
        ContractArray: [],
        LiftingArray: [],
        isEditMode: false,
        isEditingEnabled: false,
        ContractNo: contractId,
      }))
    }
  }

  // Process LiftingArray data when it changes
  useEffect(() => {
    // Only process if we're in edit mode (contract is selected) or if LiftingArray has actual data
    if (
      !state.isEditMode &&
      (!state.LiftingArray || state.LiftingArray.length === 0)
    ) {
      // Reset to empty state on initial load
      setLiftingRows([
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
      ])
      setState(prev => ({
        ...prev,
        totalLiftingQty: 0,
        DiffAmt: "0.00",
        showLiftingAlert: false,
        liftingAlertMessage: "",
      }))
      return
    }

    console.log("LiftingArray changed:", state.LiftingArray)

    let totalLiftingQty = 0
    let liftingRowsData = []

    if (state.LiftingArray && state.LiftingArray.length > 0) {
      // Process all lifting records from LiftingArray
      state.LiftingArray.forEach((record, index) => {
        console.log(`Processing lifting record ${index}:`, record)
        if (
          record.Date1 ||
          record.LorryNo ||
          record.BNo ||
          record.Lifted ||
          record.Rate1 ||
          record.DiffAmt ||
          record.AvgRate
        ) {
          const liftingRowData = {
            Date1: formatDateForInput(record.Date1 || ""),
            LorryNo: record.LorryNo || "",
            BNo: record.BNo || "",
            Lifted: record.Lifted || "",
            Rate1: record.Rate1 || "",
            DiffAmt: record.DiffAmt || "",
            AvgRate: record.AvgRate || "",
          }

          liftingRowsData.push(liftingRowData)
          totalLiftingQty += parseFloat(record.Lifted || 0)
          console.log(`Added lifting row:`, liftingRowData)
        }
      })

      console.log("Total lifting rows found:", liftingRowsData.length)
      console.log("Total lifting quantity:", totalLiftingQty)
    }

    // Set lifting rows based on the processed data
    if (liftingRowsData.length > 0) {
      setLiftingRows(liftingRowsData)
    } else {
      setLiftingRows(createDefaultLiftingRows(7))
    }

    // Only calculate DiffAmt and show alerts if we have a contract selected
    if (state.isEditMode && (state.Qty > 0 || state.contractQty > 0)) {
      // Calculate DiffAmt
      const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
      let totalDiffAmt = 0

      liftingRowsData.forEach(row => {
        const liftedQty = parseFloat(row.Lifted || 0)
        const liftingRate = parseFloat(row.Rate1 || 0)
        if (liftedQty > 0 && liftingRate > 0) {
          // Calculate difference for this row: (lifting rate - invoice rate) * quantity
          const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
          totalDiffAmt += rowDiffAmt
        }
      })

      const diffAmt = totalDiffAmt
      console.log("diffAmt", diffAmt)

      // Update lifting validation state
      setState(prev => ({
        ...prev,
        totalLiftingQty,
        DiffAmt: diffAmt.toFixed(2),
        showLiftingAlert: totalLiftingQty > (prev.contractQty || prev.Qty || 0),
        liftingAlertMessage:
          totalLiftingQty > (prev.contractQty || prev.Qty || 0)
            ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${prev.contractQty || prev.Qty || 0
            }). Please adjust.`
            : "",
      }))
    } else {
      // Reset calculations when no contract is selected
      setState(prev => ({
        ...prev,
        totalLiftingQty,
        DiffAmt: "0.00",
        showLiftingAlert: false,
        liftingAlertMessage: "",
      }))
    }
  }, [state.LiftingArray, state.isEditMode, state.Qty, state.contractQty])

  // Function to console log contract details
  const handleFillDoDetails = async () => {
    const DoDetails = {
      Id: state.id,
      ContractNo: state.ContractNo,
      Date: state.Date,
      Party: state.PartyAccountArray.find(p => p.Id === state.F_PartyLedger)?.Name || "Not Selected",
      Broker: state.BrokerAccountArray.find(b => b.Id === state.F_BrokerLedger)?.Name || "Not Selected",
      ContractType: state.TaxAccountArray.find(c => c.Id === state.F_ContractLedger)?.Name || "Not Selected",
      Commodity: state.ItemArray.find(i => i.Id === state.F_ItemType)?.Name || "Not Selected",
      Quantity: state.Qty,
      Unit: state.UnitArray.find(u => u.Id === state.F_UnitMaster)?.Name || "Not Selected",
      Vessel: state.Vessel,
      DeliveryPort: state.DeliveryPort,
    };

    // Set DoDetails in state
    setState(prev => ({ ...prev, DoDetails: DoDetails }));

    // Console log to confirm it's been saved
    console.log("DoDetails saved to state:", DoDetails);

    // First fetch existing data from API
    let NewDoDetailsArray = await Fn_FillListData(dispatch, setState, "DoDetailsArray", `${API_URL12}/Id/${state.id}`)
    console.log("DoDetailsArray:", NewDoDetailsArray);

    // If no existing data or empty array, add one empty row
    // if (!NewDoDetailsArray || NewDoDetailsArray.length === 0) {
    //   const obj = {
    //     DoNo : '',
    //     DoDate : '',
    //     Qty : '',
    //     TankNo : '',
    //     TransName : '',
    //     LogisticsFirm : '',
    //   }
    //   NewDoDetailsArray = [obj]; // Only one row
    // }

    // Update state with array (either existing data or one new row)
    setState(prev => ({ ...prev, DoDetailsArray: NewDoDetailsArray }));

    // Wait a moment for state to update, then open modal
    setTimeout(() => {
      setDoDetailsModal(true);
      // Focus on new row's DoNo field after modal opens
      setTimeout(() => {
        if (doNoInputRef.current) {
          doNoInputRef.current.focus();
        }
      }, 200);
    }, 100);
  }

  // Handle adding new row to DoDetailsArray
  const handleAddNewDoDetailsRow = async () => {
    const ItemId = state.ItemArray.find(i => i.Id === state.F_ItemType)?.Id || 0;
    const res = await Fn_FillListData(
      dispatch,
      setState,
      "DoNoNew",
      `${API_URL13}/${state.id}/${ItemId}`
    )
    console.log("DoNoNew:", res);
    let DoNo = res && res.length > 0 ? res[0].NewDoNo : '';

    const newRow = {
      DoNo: DoNo,
      DoDate: '',
      Qty: '',
      TankNo: '',
      TransName: '',
      LogisticsFirm: '',
    };

    setState(prev => ({
      ...prev,
      DoDetailsArray: [...prev.DoDetailsArray, newRow]
    }));
  }

  // Handle DoDetails table input changes
  const handleDoDetailsInputChange = (index, field, value) => {
    setState(prev => {
      const updatedArray = [...prev.DoDetailsArray];

      // If quantity is being changed, validate total doesn't exceed contract quantity
      if (field === "Qty") {
        const newQty = parseFloat(value) || 0;
        const contractQty = parseFloat(prev.DoDetails.Quantity || prev.Qty || 0);

        // Calculate total quantity of all rows (excluding current row being edited)
        let totalQty = 0;
        updatedArray.forEach((row, idx) => {
          if (idx !== index) {
            totalQty += parseFloat(row.Qty || 0);
          }
        });

        // Add the new quantity being entered
        totalQty += newQty;

        // If total exceeds contract quantity, don't update and show error
        if (totalQty > contractQty) {
          alert(`Total quantity (${totalQty}) cannot exceed Contract quantity (${contractQty}).`);
          return prev; // Don't update state
        }
      }

      updatedArray[index] = { ...updatedArray[index], [field]: value };
      return { ...prev, DoDetailsArray: updatedArray };
    });
  }

  // Handle edit button click
  const handleEditDoDetails = (index) => {
    setEditingRowIndex(index);
  }

  // Single function to handle both save and update
  const handleSaveUpdateDoDetails = async (index) => {
    const rowData = state.DoDetailsArray[index];
    const isNewRow = !rowData.Id; // Check if it's a new row (no ID)

    // Validate total quantity before saving
    const contractQty = parseFloat(state.DoDetails.Quantity || state.Qty || 0);
    let totalQty = 0;

    state.DoDetailsArray.forEach((row) => {
      totalQty += parseFloat(row.Qty || 0);
    });

    // Check if total exceeds contract quantity
    if (totalQty > contractQty) {
      alert(`Total quantity (${totalQty}) cannot exceed Contract quantity (${contractQty}). Please adjust the quantities.`);
      return; // Don't proceed with save/update
    }

    // Prepare data for API call
    const apiData = {
      ...rowData,
      Id: isNewRow ? 0 : rowData.Id, // 0 for new row, existing ID for update
      F_ContractH: state.id, // Add contract reference
      DoDate: convertDateToSQLServerFormat(rowData.DoDate) // Convert date to SQL Server format (YYYY-MM-DD)
    };

    console.log(isNewRow ? "Saving new row:" : "Updating row:", apiData);

    // Create FormData for API call
    const vFormData = new FormData();
    vFormData.append("Id", apiData.Id || 0);
    vFormData.append("DoNo", apiData.DoNo || "");
    vFormData.append("DoDate", apiData.DoDate || "");
    vFormData.append("Qty", apiData.Qty || "");
    vFormData.append("TankNo", apiData.TankNo || "");
    vFormData.append("TransName", apiData.TransName || "");
    vFormData.append("LogisticsFirm", apiData.LogisticsFirm || "");
    vFormData.append("F_ContractH", state.id);

    try {
      const res = await Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: 0, formData: vFormData } },
        'AddDoDetails/0/token',
        true,
        "AddDoDetails",
        navigate,
        "#"
      );

      console.log("API Response:", res);

      // Update state after successful save/update
      setState(prev => {
        const updatedArray = [...prev.DoDetailsArray];
        if (isNewRow) {
          // For new row, assign a temporary ID after save
          updatedArray[index] = { ...updatedArray[index], Id: Date.now() };
        } else {
          // For existing row, just update the data
          updatedArray[index] = { ...rowData };
        }
        return { ...prev, DoDetailsArray: updatedArray };
      });

      setEditingRowIndex(null);

      // Focus back to DoNo field after save/update
      setTimeout(() => {
        if (doNoInputRef.current) {
          doNoInputRef.current.focus();
        }
      }, 100);

    } catch (error) {
      console.error("Error saving/updating DoDetails:", error);
      // Optionally show error message to user
    }
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRowIndex(null);
  }

  // Handle print button for individual row
  // Handle PDF button for individual row
  const handlePDFDoDetails = (index) => {
    const rowData = state.DoDetailsArray[index];
    if (!rowData) return;

    // Get dynamic company details from GlobalArray or fallback to defaults
    const companyDetails = state.GlobalArray?.[0] || {};
    const companyName = companyDetails.Name || 'COMPANY NAME';
    const companyAddress = [
      companyDetails.Address1,
      companyDetails.Address2,
      companyDetails.City,
      companyDetails.Pincode
    ].filter(Boolean).join(', ') || 'COMPANY ADDRESS';

    // ---- Build PDF using jsPDF directly ----
    const contractDetails = {
      ContractNo: state.ContractNo || 'N/A',
      Party: state.Type === 1
        ? (state.PartyAccountArray1?.[0]?.Name || state.PartyAccountArray?.find(p => p.Id == state.F_PartyLedger)?.Name || 'N/A')
        : (state.PartyAccountArray2?.[0]?.Name || state.PartyAccountArray?.find(p => p.Id == state.F_BuyerLedger)?.Name || 'N/A'),
      Broker: state.BrokerAccountArray2?.[0]?.Name || state.BrokerAccountArray?.find(b => b.Id == state.F_BrokerLedger)?.Name || 'N/A',
      Commodity: state.ItemArray?.find(item => item.Id == state.F_ItemType)?.Name || 'N/A',
      Quantity: String(state.Qty || '0'),
      DeliveryPort: state.DeliveryPort || 'N/A',
      Vessel: state.Vessel || 'N/A',
      ContractType: (state.ContractHNo && state.ContractHNo.length > 0) ? 'REGISTERED' : 'UNREGISTERED',
    };
    const partyLabel = state.Type === 1 ? 'Seller' : 'Buyer';
    const todayStr = new Date().toLocaleDateString('en-GB');
    const doDateStr = rowData.DoDate
      ? (() => { const p = rowData.DoDate.split('-'); return p.length === 3 ? (p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0]}` : rowData.DoDate) : rowData.DoDate; })()
      : todayStr;

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = 15;

    // ---- helpers ----
    const textC = (txt, yy) => { doc.text(String(txt), pageW / 2, yy, { align: 'center' }); };
    const textL = (txt, xx, yy) => { doc.text(String(txt), xx, yy); };
    const textR = (txt, xx, yy) => { doc.text(String(txt), xx, yy, { align: 'right' }); };
    const line = (x1, y1, x2, y2) => { doc.line(x1, y1, x2, y2); };
    const rect = (x, y1, w, h) => { doc.rect(x, y1, w, h); };
    const labelValue = (label, value, yy, lx = margin, lw = 45) => {
      doc.setFont('helvetica', 'bold'); textL(label + ':', lx, yy);
      doc.setFont('helvetica', 'normal'); textL(String(value), lx + lw, yy);
      line(lx + lw, yy + 1, lx + contentW, yy + 1);
    };

    // ---- Company header ----
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    textC(companyName, y); y += 2;
    // underline
    const nameW = doc.getTextWidth(companyName);
    line((pageW - nameW) / 2, y, (pageW + nameW) / 2, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    textC(companyAddress, y); y += 10;

    // separator
    line(margin, y, pageW - margin, y); y += 6;

    // ---- Ref / Date row ----
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold'); textL('Ref No.:', margin, y);
    doc.setFont('helvetica', 'normal'); textL(rowData.DoNo || 'S-XXX', margin + 20, y);
    textR(todayStr, pageW - margin, y); y += 8;

    // ---- TO section ----
    doc.setFont('helvetica', 'bold'); textL('TO', margin, y); y += 5;
    doc.setFont('helvetica', 'normal');
    textL(rowData.LogisticsFirm || 'LOGISTICS FIRM', margin, y); y += 5;
    textL(contractDetails.DeliveryPort, margin, y); y += 8;

    // ---- Instruction ----
    doc.setFont('helvetica', 'bold');
    textL('Please deliver our Quantity the details are given below:', margin, y);
    doc.setFont('helvetica', 'normal'); y += 8;

    // ---- Importer row ----
    doc.setFont('helvetica', 'bold'); textL('Importer :', margin, y);
    doc.setFont('helvetica', 'normal'); textL(companyName, margin + 30, y);
    line(margin + 30, y + 1, margin + 120, y + 1);
    // ContractType box on right
    const cTypeText = contractDetails.ContractType;
    const cTypeW = doc.getTextWidth(cTypeText) + 6;
    rect(pageW - margin - cTypeW, y - 5, cTypeW, 7);
    doc.setFont('helvetica', 'bold'); textL(cTypeText, pageW - margin - cTypeW + 3, y);
    doc.setFont('helvetica', 'normal'); y += 8;

    // ---- Party ----
    labelValue(partyLabel, contractDetails.Party, y); y += 8;

    // ---- Vessel (boxed) ----
    doc.setFont('helvetica', 'bold'); textL('Vessel :', margin, y);
    doc.setFont('helvetica', 'normal');
    const vesselText = contractDetails.Vessel;
    const vesselW = Math.max(doc.getTextWidth(vesselText) + 8, 60);
    rect(margin + 22, y - 5, vesselW, 7);
    textL(vesselText, margin + 25, y); y += 9;

    // ---- Quantity + Tank ----
    doc.setFont('helvetica', 'bold'); textL('Quantity :', margin, y);
    doc.setFont('helvetica', 'normal'); textL(contractDetails.Quantity, margin + 28, y);
    line(margin + 28, y + 1, margin + 68, y + 1);
    const tankText = 'TANK NO. ' + (rowData.TankNo || 'XXX');
    const tankW = doc.getTextWidth(tankText) + 6;
    rect(pageW - margin - tankW, y - 5, tankW, 7);
    doc.setFont('helvetica', 'bold'); textL(tankText, pageW - margin - tankW + 3, y);
    doc.setFont('helvetica', 'normal'); y += 9;

    // ---- Commodity ----
    labelValue('Commodity', contractDetails.Commodity, y); y += 8;

    // ---- Port + Broker/Date ----
    doc.setFont('helvetica', 'bold'); textL('Port :', margin, y);
    doc.setFont('helvetica', 'normal'); textL(contractDetails.DeliveryPort, margin + 18, y);
    line(margin + 18, y + 1, margin + 80, y + 1);
    doc.setFont('helvetica', 'bold'); textR('BAR DT.  ' + doDateStr, pageW - margin, y); y += 5;
    doc.setFont('helvetica', 'normal'); textR('BROKER  ' + contractDetails.Broker, pageW - margin, y); y += 9;

    // ---- Transporter ----
    doc.setFont('helvetica', 'bold'); textL('Transporter :', margin, y);
    doc.setFont('helvetica', 'normal');
    textL('AS PER BUYER INSTRUCTION / ' + (rowData.TransName || 'TRANSPORT NAME'), margin + 33, y);
    line(margin + 33, y + 1, margin + contentW, y + 1); y += 10;

    // ---- Notice box ----
    const noticeLines = [
      'IMPORTANT NOTICE: BEFORE LIFTING SAMPLE DRAWN BECAUSE OUT OF TERMINAL',
      'CARGO QUALITY COMPLAINT NOT ACCEPTED.'
    ];
    const noticeH = 16;
    rect(margin, y, contentW, noticeH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    textC(noticeLines[0], y + 6);
    textC(noticeLines[1], y + 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10); y += noticeH + 8;

    // ---- Kindly ----
    doc.setFont('helvetica', 'bold');
    textL('Kindly do the needful & oblige us.', margin, y);
    doc.setFont('helvetica', 'normal'); y += 15;

    // ---- Footer ----
    textL('Thanking you', margin, y); y += 5;
    textL('Yours truly,', margin, y); y += 5;
    doc.setFont('helvetica', 'bold');
    textL('For ' + companyName, margin, y);

    doc.setFont('helvetica', 'bold');
    textR('OUR OFFICE ADDRESS', pageW - margin, y - 10);
    const addrX = pageW - margin;
    const addrY = y - 5;
    // underline the header
    const addrTitleW = doc.getTextWidth('OUR OFFICE ADDRESS');
    line(addrX - addrTitleW, addrY - 3, addrX, addrY - 3);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    // wrap address text
    const addrLines = doc.splitTextToSize(companyAddress, 80);
    addrLines.forEach((l, i) => { textR(l, addrX, addrY + i * 5); });

    // ---- Output ----
    const filename = `DO_Details_${rowData.DoNo || 'New'}.pdf`;
    const pdfBlob = doc.output('blob');
    const file = new File([pdfBlob], filename, { type: 'application/pdf' });
    if (navigator.share) {
      setState(prev => ({ ...prev, pendingShareFile: file, showSharePDFModal: true }));
    } else {
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toastr.success('PDF downloaded successfully!');
    }
  }

  const handleInputChange = async e => {
    const { name, value, type, checked } = e.target

    // For checkboxes
    const newValue = type === "checkbox" ? checked : value

    // Update state directly for all fields including notes
    setState(prev => ({ ...prev, [name]: newValue }))

    // Auto-update InvRate when Rate is changed
    if (name === "Rate") {
      setState(prev => ({ ...prev, InvRate: value || 0 }))

      // Recalculate DiffAmt when contract rate changes
      const invoiceRate = parseFloat(value || 0)
      let totalDiffAmt = 0

      liftingRows.forEach(row => {
        const liftedQty = parseFloat(row.Lifted || 0)
        const liftingRate = parseFloat(row.Rate1 || 0)
        if (liftedQty > 0 && liftingRate > 0) {
          // Calculate difference for this row: (lifting rate - invoice rate) * quantity
          const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
          totalDiffAmt += rowDiffAmt
        }
      })

      const diffAmt = totalDiffAmt

      setState(prev => ({
        ...prev,
        InvRate: value || 0,
        DiffAmt: diffAmt.toFixed(2),
      }))
    }

    // Recalculate lifting quantities when contract quantity changes
    if (name === "Qty") {
      const contractQty = parseFloat(value || 0)
      const totalLiftingQty = liftingRows.reduce((sum, row) => {
        return sum + parseFloat(row.Lifted || 0)
      }, 0)

      setState(prev => ({
        ...prev,
        contractQty,
        totalLiftingQty,
        showLiftingAlert: totalLiftingQty > contractQty,
        liftingAlertMessage:
          totalLiftingQty > contractQty
            ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
            : "",
      }))
    }

    // Handle ContractNo changes - allow editing for new contracts
    if (name === "ContractNo") {
      setState(prev => ({
        ...prev,
        ContractId: "", // Clear dropdown selection when manually typing
        ContractArray: [], // Clear any loaded contract data
        LiftingArray: [], // Clear lifting data
        isEditMode: false, // Reset to add mode
        // Keep the form data but allow contract number to be changed
      }))
    }

    if (name === "F_PartyLedger" && value) {
      // Call API with selected ID for PartyAccountArray1
      await Fn_FillListData(
        dispatch,
        setState,
        "PartyAccountArray1",
        `${API_URL6}/Id/${value}`
      )
    }

    if (name === "F_BrokerLedger" && value) {
      // Call API with selected ID for TaxAccountArray1
      await Fn_FillListData(
        dispatch,
        setState,
        "BrokerAccountArray2",
        `${API_URL6}/Id/${value}`
      )
    }

    if (name === "ContractId" && value) {
      // Check if this is an existing contract by trying to load it
      try {
        await Fn_FillListData(
          dispatch,
          setState,
          "ContractArray",
          `${API_URL8}/Id/${value}`
        )

        // After ContractArray is updated, check if we found a contract
        setTimeout(async () => {
          setState(prev => {
            const contract = prev.ContractArray[0] || {}

            // If we found a contract, load its data for editing
            if (contract.Id) {
              // Load contact person data for seller and buyer
              if (contract.F_PartyLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray1",
                  `${API_URL6}/Id/${contract.F_PartyLedger}`
                )
              }

              if (contract.F_BrokerLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "BrokerAccountArray2",
                  `${API_URL6}/Id/${contract.F_BrokerLedger}`
                )
              }
              if (contract.LiftingLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray",
                  `${API_URL6}/Id/${contract.LiftingLedger}`
                )
              }

              // Fetch lifting data using ContractH ID
              Fn_FillListData(
                dispatch,
                setState,
                "LiftingArray",
                `${API_URL10}/Id/${contract.Id}`
              )

              return {
                ...prev,
                id: contract.Id || 0,
                Date:
                  formatDateForInput(contract.Date) ||
                  new Date().toISOString().split("T")[0],
                ContractNo: contract.ContractNo || "",
                BroContNo: contract.BroContNo || "",
                BroContDate: contract.BroContDate || "",
                ContractId: value, // Keep the selected ID
                F_PartyLedger: contract.F_PartyLedger || 0,
                F_BrokerLedger: contract.F_BrokerLedger || 0,
                F_ItemType: contract.F_ItemType || 0,
                Qty: contract.Qty || 0,
                Rate: contract.Rate || 0,
                InvRate: contract.InvRate || contract.Rate || 0,
                F_UnitMaster: contract.F_UnitMaster || 0,
                F_MonthMaster: contract.F_MonthMaster || 0,
                F_YearMaster: contract.F_YearMaster || 0,
                F_YearMaster: contract.F_YearMaster || 0,
                Vessel: contract.Vessel || "",
                DeliveryPort: contract.DeliveryPort || "",
                ShipMentFromDate: contract.ShipMentFromDate || "",
                ShipMentToDate: contract.ShipMentToDate || "",
                LiftedFromDate: contract.LiftedFromDate || "",
                LiftedToDate: contract.LiftedToDate || "",
                F_ContractLedger: contract.F_ContractLedger || 0,
                Remarks: contract.Remarks || "",
                AdvPayment: contract.AdvPayment || "",
                AdvDate: contract.AdvDate || "",
                Note5: contract.Note5 || "",
                Note6: contract.Note6 || "",
                importDuty: contract.importDuty || 0,
                tariff: contract.tariff || 0,
                exchangeRate: contract.exchangeRate || 0,
                calPerTonDuty: contract.calPerTonDuty || 0,
                isEditMode: true,
                isEditingEnabled: false, // Reset editing enabled when loading contract
                contractQty: contract.Qty || 0,
                BackSellerCNo: contract.BackSellerCNo || "",
                SellerPerson: contract.SellerPerson || "",
                BuyerPerson: contract.BuyerPerson || "",
                LiftingLedger: contract.LiftingLedger || "",
                Type: contract.Type || 1


              }
            } else {
              // No existing contract found, this is a new contract number
              return {
                ...prev,
                id: 0,
                ContractArray: [],
                LiftingArray: [],
                isEditMode: false,
                // Keep the manually entered contract number
                ContractNo: "",
                originalContractNo: "", // Reset original contract number
                LiftingLedger: "",
              }
            }
          })
        }, 200)
      } catch (error) {
        // If API call fails, treat as new contract
        console.log("No existing contract found, treating as new contract")
        setState(prev => ({
          ...prev,
          id: 0,
          ContractArray: [],
          LiftingArray: [],
          isEditMode: false,
          ContractNo: "",
          originalContractNo: "", // Reset original contract number
          LiftingLedger: "",
        }))
      }
    } else if (name === "ContractId" && !value) {
      // Clear all data when no contract is selected
      setLiftingRows(createDefaultLiftingRows(7))
      setState(prev => ({
        ...prev,
        ContractArray: [],
        LiftingArray: [],
        totalLiftingQty: 0,
        showLiftingAlert: false,
        liftingAlertMessage: "",
        isEditMode: false,
        isEditingEnabled: false, // Reset editing enabled when clearing form
        // Reset ContractNo to ContractNoNew for new contracts
        ContractNo: "",
        originalContractNo: "", // Reset original contract number
        LiftingLedger: "",
      }))
    }
  }

  // Modal toggle functions
  const toggleSellerModal = () => {
    setSellerModal(!sellerModal)
    if (!sellerModal) {
      setNewPartyData({ Name: "", Person: "", Address: "", GSTNo: "" })
    }
  }

  const toggleBrokerModal = () => {
    setBrokerModal(!brokerModal)
    if (!brokerModal) {
      setNewBrokerData({ Name: "", Person: "", Address: "", GSTNo: "" })
    }
  }

  const toggleContractModal = () => {
    setContractModal(!contractModal)
    if (!contractModal) {
      setNewContractData({ Name: "" })
    }
  }

  const handleContractModalInputChange = (field, value) => {
    setNewContractData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCommodityModalInputChange = (field, value) => {
    setNewCommodityData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const toggleCommodityModal = () => {
    setCommodityModal(!commodityModal)
    if (!commodityModal) {
      setNewCommodityData({ Name: "", Alias: "" })
    }
  }

  // Handle modal form input changes
  const handleModalInputChange = (field, value) => {
    setNewPartyData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleBrokerModalInputChange = (field, value) => {
    setNewBrokerData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  // Save new party data
  const handleSaveNewParty = async () => {
    const formData = new FormData()
    formData.append("Name", newPartyData.Name || "")
    formData.append("Person", newPartyData.Person || "")
    formData.append("Address", newPartyData.Address || "")
    formData.append("GSTNo", newPartyData.GSTNo || "")
    formData.append("F_LedgerGroupMaster", 48)

    const res = await Fn_AddEditData(
      dispatch,
      setNewPartyData,
      { arguList: { id: 0, formData } },
      API_SaveLedger,
      true,
      "NewTender",
      navigate,
      "#"
    )

    setSellerModal(false)
    Fn_FillListData(dispatch, setState, "PartyAccountArray", API_URL + "/Id/0")
    Fn_FillListData(dispatch, setState, "BrokerAccountArray", API_URLBroker + "/Id/0")
  }

  // Save new broker data
  const handleSaveNewBroker = async () => {
    const formData = new FormData()
    formData.append("Name", newBrokerData.Name || "")
    formData.append("Person", newBrokerData.Person || "")
    formData.append("Address", newBrokerData.Address || "")
    formData.append("GSTNo", newBrokerData.GSTNo || "")
    formData.append("F_LedgerGroupMaster", 9)

    const res = await Fn_AddEditData(
      dispatch,
      setNewBrokerData,
      { arguList: { id: 0, formData } },
      API_SaveLedger,
      true,
      "NewTender",
      navigate,
      "#"
    )

    setBrokerModal(false)
    Fn_FillListData(dispatch, setState, "BrokerAccountArray", API_URLBroker + "/Id/0")
    Fn_FillListData(dispatch, setState, "PartyAccountArray", API_URL + "/Id/0")
  }

  // Save new contract data
  const handleSaveNewContract = async () => {
    const formData = new FormData()
    formData.append("Name", newContractData.Name || "")
    formData.append("F_LedgerGroupMaster", 20)

    const res = await Fn_AddEditData(
      dispatch,
      setNewContractData,
      { arguList: { id: 0, formData } },
      API_SaveLedger,
      true,
      "NewTender",
      navigate,
      "#"
    )

    setContractModal(false)
    Fn_FillListData(dispatch, setState, "TaxAccountArray", API_URL1 + "/Id/0")
  }


  // Save new contract data
  const handleSaveNewCommodity = async () => {
    const formData = new FormData()
    formData.append("Name", newCommodityData.Name || "")
    formData.append("Alias", newCommodityData.Alias || "")


    const res = await Fn_AddEditData(
      dispatch,
      setNewCommodityData,
      { arguList: { id: 0, formData } },
      API_SaveCommodity,
      true,
      "NewTender",
      navigate,
      "#"
    )

    setCommodityModal(false)
    Fn_FillListData(dispatch, setState, "ItemArray", API_URL3 + "/Id/0")
  }

  // Function to handle updating existing contract with new contract number
  const handleUpdateExistingContract = () => {
    // Check if user is trying to update an existing contract with a new contract number


    // Normal save/update
    handleSave()
  }


  // New function to enable editing
  const handleEnableEditing = () => {
    setState(prev => ({
      ...prev,
      isEditingEnabled: true,
    }))
  }

  const handleSave = async () => {
    try {
      setState(prev => ({
        ...prev,
        isProgress: true,
        showSuccessMessage: false,
        showErrorMessage: false,
      }))

      const formData = new FormData()

      // Get current form values from DOM to ensure we have the latest data
      const getCurrentFormValue = (fieldName) => {
        const element = document.querySelector(`[name="${fieldName}"]`)
        return element ? element.value : ""
      }

      // Use current state values and DOM values for editing
      formData.append("Date", getCurrentFormValue("Date") || state.Date || "")
      formData.append("Type", state.Type || 1) // 1 for Buy, 2 for Sell

      // Handle ContractNo based on whether ContractId is selected or not
      let contractNoToSave = getCurrentFormValue("ContractCombined") || state.ContractNo || ""

      // Determine if this is a new contract or update
      const isNewContract = !state.ContractId || state.ContractId === ""
      const isContractNumberChanged = state.contractNumberChanged && state.ContractNo !== state.originalContractNo

      // If contract number changed and we're in edit mode, treat as new contract
      if (state.isEditMode && isContractNumberChanged) {
        // Reset ID to 0 to create new contract
        formData.append("Id", 0)
      } else if (state.isEditMode && state.id) {
        // Use existing ID for update when in edit mode
        formData.append("Id", state.id)
      } else {
        // Use existing ID for update or 0 for new contract
        formData.append("Id", state.id || 0)
      }

      formData.append("ContractNo", contractNoToSave)
      formData.append("BroContNo", getCurrentFormValue("BroContNo") || state.BroContNo || "")
      formData.append("BroContDate", getCurrentFormValue("BroContDate") || state.BroContDate || "")
      formData.append(
        "F_UnitMaster",
        state.F_UnitMaster || 0
      )
      formData.append("F_PartyLedger", getCurrentFormValue("F_PartyLedger") || state.F_PartyLedger || 0)
      formData.append("F_BrokerLedger", getCurrentFormValue("F_BrokerLedger") || state.F_BrokerLedger || 0)
      formData.append("F_ItemType", getCurrentFormValue("F_ItemType") || state.F_ItemType || 0)
      formData.append("Qty", getCurrentFormValue("Qty") || state.Qty || 0)
      formData.append("Rate", getCurrentFormValue("Rate") || state.Rate || 0)
      formData.append("InvRate", state.InvRate || getCurrentFormValue("Rate") || state.Rate || 0)
      formData.append("F_MonthMaster", getCurrentFormValue("F_MonthMaster") || state.F_MonthMaster || 0)
      formData.append("F_YearMaster", getCurrentFormValue("F_YearMaster") || state.F_YearMaster || 0)
      formData.append("Vessel", getCurrentFormValue("Vessel") || state.Vessel || "")
      formData.append("DeliveryPort", getCurrentFormValue("DeliveryPort") || state.DeliveryPort || "")
      formData.append("ShipMentFromDate", getCurrentFormValue("ShipMentFromDate") || state.ShipMentFromDate || "")
      formData.append("ShipMentToDate", getCurrentFormValue("ShipMentToDate") || state.ShipMentToDate || "")
      formData.append("LiftedFromDate", getCurrentFormValue("LiftedFromDate") || state.LiftedFromDate || "")
      formData.append("LiftedToDate", getCurrentFormValue("LiftedToDate") || state.LiftedToDate || "")
      formData.append("F_ContractLedger", getCurrentFormValue("F_ContractLedger") || state.F_ContractLedger || 0)
      formData.append("Remarks", getCurrentFormValue("Remarks") || state.Remarks || "")
      formData.append("AdvPayment", getCurrentFormValue("AdvPayment") || state.AdvPayment || "")
      formData.append("AdvDate", getCurrentFormValue("AdvDate") || state.AdvDate || "")
      formData.append("BackSellerCNo", getCurrentFormValue("BackSellerCNo") || state.BackSellerCNo || "")
      formData.append("importDuty", getCurrentFormValue("importDuty") || state.importDuty || 0)
      formData.append("tariff", getCurrentFormValue("tariff") || state.tariff || 0)
      formData.append("exchangeRate", getCurrentFormValue("exchangeRate") || state.exchangeRate || 0)
      formData.append("calPerTonDuty", getCurrentFormValue("calPerTonDuty") || state.calPerTonDuty || 0)
      formData.append("SellerPerson", state.PartyAccountArray1?.[0]?.Person || "")
      formData.append("BuyerPerson", state.BrokerAccountArray2?.[0]?.Person || "")
      formData.append("Note5", getCurrentFormValue("Note5") || state.Note5 || "")
      formData.append("Note6", getCurrentFormValue("Note6") || state.Note6 || "")
      formData.append("Note1", getCurrentFormValue("Note1") || state.Note1 || "")
      formData.append("Note2", getCurrentFormValue("Note2") || state.Note2 || "")
      formData.append("Note3", getCurrentFormValue("Note3") || state.Note3 || "")
      formData.append("Note4", getCurrentFormValue("Note4") || state.Note4 || "")
      formData.append("LiftingLedger", getCurrentFormValue("LiftingLedger") || state.LiftingLedger || 0)
      // Determine the correct ID for the API call
      let apiId = 0
      if (state.isEditMode && !isContractNumberChanged && state.id) {
        // Update existing contract - use the actual contract ID
        apiId = state.id
      } else if (isNewContract || (state.isEditMode && isContractNumberChanged)) {
        // Create new contract - use 0
        apiId = 0
      } else {
        // Fallback - use 0 for new contracts
        apiId = 0
      }

      // Create dynamic API URL with the correct ID
      const dynamicApiUrl = `${API_WEB_URLS.ContractH}/${apiId}/token`

      const res = await Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: apiId, formData } },
        dynamicApiUrl,
        true,
        "NewTender",
        navigate,
        "#"
      )
      debugger
      console.log("res", res)

      // Show success message based on operation type
      if (res && res.success) {
        if (isNewContract || (state.isEditMode && isContractNumberChanged)) {
          const message = isContractNumberChanged
            ? `New contract ${contractNoToSave} created successfully from contract ${state.originalContractNo}!`
            : `New contract ${contractNoToSave} created successfully!`

          setState(prev => ({
            ...prev,
            showSuccessMessage: true,
            message: message,
            isProgress: false,
          }))
          toastr.success(message)
        } else {
          setState(prev => ({
            ...prev,
            showSuccessMessage: true,
            message: `Contract ${contractNoToSave} updated successfully!`,
            isProgress: false,
          }))
          toastr.success(`Contract ${contractNoToSave} updated successfully!`)
        }

        // Reset the contract number changed flag after successful save
        setState(prev => ({
          ...prev,
          contractNumberChanged: false,
          originalContractNo: contractNoToSave, // Update to the new contract number
        }))

        // Call onClose if loaded from a modal
        if (isModal && onClose) {
          setTimeout(() => {
            onClose()
          }, 1000)
        }
      }

      // Get contract quantity for validation
      const contractQty = parseFloat(
        getCurrentFormValue("Qty") || state.Qty || state.ContractArray?.[0]?.Qty || 0
      )

      // Calculate total lifting quantity
      const validLiftingRows = liftingRows.filter(
        row => row.Date1 || row.LorryNo || row.BNo || row.Lifted || row.Rate1
      )

      const totalLiftingQty = validLiftingRows.reduce((sum, row) => {
        return sum + parseFloat(row.Lifted || 0)
      }, 0)

      // Validate lifting quantity
      if (totalLiftingQty > contractQty) {
        setState(prev => ({
          ...prev,
          isProgress: false,
          showLiftingAlert: true,
          liftingAlertMessage: `Lifting quantity (${totalLiftingQty}) cannot exceed contract quantity (${contractQty}). Please adjust the lifting quantities.`,
        }))
        return
      }

      // Save lifting data with the response ID from ContractH in JSON format
      if (validLiftingRows.length > 0) {
        const liftingDataArray = validLiftingRows.map(row => {
          const invoiceRate = parseFloat(state.InvRate || getCurrentFormValue("Rate") || state.Rate || 0)
          const liftingRate = parseFloat(row.Rate1 || 0)
          const liftingQty = parseFloat(row.Lifted || 0)
          // Calculate difference: (lifting rate - invoice rate) * quantity
          const diffAmt = (liftingRate - invoiceRate) * liftingQty

          return {
            // F_ContractH: res.id || res.Id || state.id,
            Date: row.Date1 || "",
            LorryNo: row.LorryNo || "",
            BNo: row.BNo || "",
            Lifted: parseFloat(row.Lifted || 0),
            Rate: parseFloat(row.Rate1 || 0),
            DiffAmt: parseFloat(diffAmt.toFixed(2)),
            LiftingLedger: getCurrentFormValue("LiftingLedger") || state.LiftingLedger || 0,
          }
        })

        // Send lifting data as JSON array
        const liftingFormData = new FormData()
        liftingFormData.append("Data", JSON.stringify(liftingDataArray))
        liftingFormData.append("F_ContractH", res.id || res.Id || state.id)

        await Fn_AddEditData(
          dispatch,
          setState,
          { arguList: { id: 0, formData: liftingFormData } },
          API_URL_Lifting,
          true,
          "NewTender",
          navigate,
          "#"
        )
      }

      setState(prev => ({
        ...prev,
        isProgress: false,
        showSuccessMessage: true,
        showLiftingAlert: false,
        liftingAlertMessage: "",
        message: state.isEditMode
          ? "Contract updated successfully!"
          : "Contract saved successfully!",
      }))

      // Auto-hide success message after 3 seconds and reload page
      setTimeout(() => {
        setState(prev => ({ ...prev, showSuccessMessage: false }))
        // Reload the page after successful save/update
        window.location.reload()
      }, 3000)
    } catch (error) {
      console.error("Error saving contract:", error)
      setState(prev => ({
        ...prev,
        isProgress: false,
        showErrorMessage: true,
        message: "Error saving contract. Please try again.",
      }))

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setState(prev => ({ ...prev, showErrorMessage: false }))
      }, 5000)
    }
  }

  const handleDelete = () => {
    console.log("Deleting contract")

    // Get the contract ID from the selected contract
    const contractId = state.ContractArray?.[0]?.Id

    if (!contractId) {
      alert("Please select a contract first to delete")
      return
    }

    // Confirm deletion
    if (window.confirm("Are you sure you want to delete this contract?")) {
      // Call the delete function with correct parameters
      Fn_DeleteData(dispatch, setState, contractId, API_Delete, true)
        .then(response => {
          console.log("Delete successful:", response)
          // After successful deletion, clear the form and refresh
          setState(prev => ({
            ...prev,
            id: 0,
            ContractArray: [],
            // Reset other form fields as needed
          }))
          // Show success message
          alert("Contract deleted successfully!")
          // Optionally navigate back or refresh the page
          navigate("/dashboard")
        })
        .catch(error => {
          console.error("Error deleting contract:", error)
          // Show error message without redirecting
          alert("Error deleting contract. Please try again.")
        })
    }
  }

  const handlePrint = () => {
    console.log("Printing contract")
    // Navigate to print component with contract ID
    if (state.id || state.ContractArray?.[0]?.Id) {
      const contractId = state.id || state.ContractArray[0].Id
      window.open(`/contract-print2?id=${contractId}`, '_blank')
    } else {
      // Show alert if no contract is selected
      alert("Please select a contract first to print")
    }
  }

  // Generate PDF from contract print page and return blob
  const generateContractPDF = async (contractId, contractNo) => {
    return new Promise((resolve, reject) => {
      try {
        // Open contract print page in hidden iframe
        const iframe = document.createElement('iframe')
        iframe.style.position = 'fixed'
        iframe.style.right = '-9999px'
        iframe.style.width = '210mm' // A4 width
        iframe.style.height = '297mm' // A4 height
        iframe.src = `${window.location.origin}/contract-print2?id=${contractId}`
        document.body.appendChild(iframe)

        iframe.onload = () => {
          let attempts = 0
          const maxAttempts = 30 // Wait up to 15 seconds (30 * 500ms)

          const checkContentInterval = setInterval(() => {
            attempts++
            try {
              const printContent = iframe.contentDocument || iframe.contentWindow.document
              const element = printContent.querySelector('.contract-print-container')

              if (element) {
                // Found the content!
                clearInterval(checkContentInterval)

                setTimeout(() => {
                  try {
                    const opt = {
                      margin: [10, 10, 10, 10],
                      filename: `Contract_${contractNo}_${contractId}.pdf`,
                      image: { type: 'jpeg', quality: 1.0 },
                      html2canvas: { scale: 2, useCORS: true, logging: true },
                      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                    }

                    html2pdf()
                      .set(opt)
                      .from(element)
                      .outputPdf('blob')
                      .then(pdfBlob => {
                        document.body.removeChild(iframe)
                        resolve(pdfBlob)
                      })
                      .catch(err => {
                        console.error('html2pdf JS error:', err)
                        toastr.error("Failed to generate PDF Blob: " + err.message)
                        document.body.removeChild(iframe)
                        reject(err)
                      })
                  } catch (pdfErr) {
                    console.error('html2pdf catch block:', pdfErr)
                    toastr.error("Crash inside html2pdf function")
                    document.body.removeChild(iframe)
                    reject(pdfErr)
                  }
                }, 500) // Small buffer to ensure images load

              } else if (attempts >= maxAttempts) {
                clearInterval(checkContentInterval)
                toastr.error("Timeout: Could not load contract data for printing.")
                document.body.removeChild(iframe)
                reject(new Error("Timeout loading contract print container"))
              }
            } catch (err) {
              clearInterval(checkContentInterval)
              console.error('Error accessing iframe DOM:', err)
              toastr.error('Error accessing iframe content: ' + err.message)
              document.body.removeChild(iframe)
              reject(err)
            }
          }, 500) // Poll every 500ms
        }

        iframe.onerror = () => {
          toastr.error('Failed to connect to contract-print2 route')
          document.body.removeChild(iframe)
          reject(new Error('Failed to load iframe url'))
        }
      } catch (err) {
        toastr.error("General PDF generator crash: " + err.message)
        reject(err)
      }
    })
  }

  // Called from Share button click – runs in user gesture so navigator.share() is allowed
  const handleSharePDFClick = async () => {
    if (!state.pendingShareFile || !navigator.share) return
    try {
      await navigator.share({
        title: 'Contract Report',
        text: 'Please find attached the Contract Report',
        files: [state.pendingShareFile]
      })
      toastr.success('PDF shared successfully!')
      setState(prev => ({ ...prev, showSharePDFModal: false, pendingShareFile: null }))
    } catch (shareError) {
      if (shareError.name === 'AbortError') {
        toastr.info('Share cancelled.')
      } else {
        console.error('Share error:', shareError)
        toastr.error('Share failed. Try again.')
      }
      setState(prev => ({ ...prev, showSharePDFModal: false, pendingShareFile: null }))
    }
  }

  // Generate PDF and open Share Modal (or download directly if share unsupported)
  const handlePDFExport = async () => {
    if (state.id || state.ContractArray?.[0]?.Id) {
      const contractId = state.id || state.ContractArray[0].Id
      const contractNo = state.ContractNo || 'Contract'

      try {
        toastr.info('Generating PDF... Please wait.')

        // Generate PDF blob
        const pdfBlob = await generateContractPDF(contractId, contractNo)

        const filename = `Contract_${contractNo}_${contractId}.pdf`
        const file = new File([pdfBlob], filename, { type: 'application/pdf' })

        if (navigator.share) {
          setState(prev => ({ ...prev, pendingShareFile: file, showSharePDFModal: true }))
        } else {
          // Fallback if no share
          const url = window.URL.createObjectURL(pdfBlob)
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          toastr.success('PDF downloaded successfully!')
        }
      } catch (error) {
        console.error('Error generating PDF:', error)
        toastr.error('Error generating PDF. Please try again or use Print button.')
      }
    } else {
      toastr.warning("Please select a contract first to generate PDF")
    }
  }

  const handleExit = () => {
    console.log("Exiting contract form")
    navigate("/dashboard")
  }

  const handleLiftingChange = (index, field, value) => {
    const updatedRows = [...liftingRows]
    updatedRows[index][field] = value
    setLiftingRows(updatedRows)

    // Calculate total lifting quantity and validate
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = updatedRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt: (Rate of each row in lifting - invoice rate) * qty of that row and then sum all differences
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    updatedRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      contractQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  const addLiftingRow = () => {
    const newRows = [
      ...liftingRows,
      { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
    ]
    setLiftingRows(newRows)

    // Recalculate totals and DiffAmt
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = newRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    newRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  const removeLiftingRow = index => {
    const updatedRows = [...liftingRows]
    updatedRows.splice(index, 1)
    setLiftingRows(updatedRows)

    // Recalculate totals and DiffAmt
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = updatedRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    updatedRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  // Handle lifting keyboard navigation
  const handleLiftingKeyDown = (e, rowIndex, fieldName) => {
    if (e.key === "Enter") {
      e.preventDefault()

      if (fieldName === "Rate1") {
        // On Rate field Enter, add new row and focus on its date
        const currentRowCount = liftingRows.length
        addLiftingRow()
        setTimeout(() => {
          // Focus on the new row's Date field (which will be at the current count index)
          const newRowDateInput = document.querySelector(
            `input[name="Date1-${currentRowCount}"]`
          )
          if (newRowDateInput) {
            newRowDateInput.focus()
          }
        }, 150)
      } else {
        // Navigate to next field in same row or next row
        const nextField = getNextLiftingField(fieldName)
        if (nextField) {
          const nextInput = document.querySelector(
            `input[name="${nextField}-${rowIndex}"]`
          )
          if (nextInput) {
            nextInput.focus()
          }
        }
      }
    }
  }

  // Get next field for lifting navigation
  const getNextLiftingField = currentField => {
    const fieldOrder = ["Date1", "LorryNo", "BNo", "Lifted", "Rate1"]
    const currentIndex = fieldOrder.indexOf(currentField)
    return currentIndex < fieldOrder.length - 1
      ? fieldOrder[currentIndex + 1]
      : null
  }

  // Wrapper functions for lifting operations
  const handleAddLiftingRow = () => {
    addLiftingRow()
  }

  const handleRemoveLiftingRow = index => {
    removeLiftingRow(index)
  }

  // Handle tab key to clear note fields
  const handleNoteKeyDown = (e, fieldName) => {
    if (e.key === "Tab") {
      e.preventDefault()
      setState(prev => ({ ...prev, [fieldName]: "" }))
      return
    }
  }

  // Handle Enter key navigation with conditional logic
  const handleKeyDown = (e, nextRef) => {
    console.log("handleKeyDown", e.key)
    if (e.key === "Enter") {
      e.preventDefault()

      const fieldName = e.target.name

      // Special handling for ContractCombined field
      if (fieldName === "ContractCombined") {
        // Check if contract number is empty
        if (!e.target.value || e.target.value.trim() === "") {
          alert("Please enter a contract number before proceeding.")
          return
        }

        // If nextRef is provided, focus on that (for Serial No -> BroContNo flow)
        if (nextRef && nextRef.current) {
          nextRef.current.focus()
          return
        }

        // Only navigate to F_PartyLedger if contract number has been changed by user
        if (state.contractNumberChanged) {
          // Contract number changed, navigate to F_PartyLedger
          if (partyRef.current) {
            partyRef.current.focus()
            return
          }
        } else {
          // Contract number unchanged or page just loaded, stay on same field
          return
        }
      }

      // Check if shipment month/year is selected
      const isShipmentMonthSelected =
        (state.F_MonthMaster && state.F_MonthMaster !== 0) ||
        (state.F_YearMaster && state.F_YearMaster !== 0)
      // Check if shipment dates are selected - check for both truthy values and non-empty strings
      const areShipmentDatesSelected = state.ShipMentFromDate && state.ShipMentToDate &&
        state.ShipMentFromDate !== "" && state.ShipMentToDate !== "" &&
        state.ShipMentFromDate !== null && state.ShipMentToDate !== null
      // Check if lifting dates are selected - check for both truthy values and non-empty strings
      const areLiftingDatesSelected = state.LiftedFromDate && state.LiftedToDate &&
        state.LiftedFromDate !== "" && state.LiftedToDate !== "" &&
        state.LiftedFromDate !== null && state.LiftedToDate !== null

      // Handle delivery port navigation based on conditions
      if (fieldName === "DeliveryPort") {
        console.log("DeliveryPort navigation - Debug info:")
        console.log(
          "isShipmentMonthSelected:",
          isShipmentMonthSelected,
          "month:",
          state.F_MonthMaster,
          "year:",
          state.F_YearMaster
        )
        console.log("areShipmentDatesSelected:", areShipmentDatesSelected, "values:", state.ShipMentFromDate, state.ShipMentToDate)
        console.log("areLiftingDatesSelected:", areLiftingDatesSelected, "values:", state.LiftedFromDate, state.LiftedToDate)

        // Also check the actual DOM values to be sure
        const shipmentFromInput = document.querySelector('input[name="ShipMentFromDate"]')
        const shipmentToInput = document.querySelector('input[name="ShipMentToDate"]')
        const liftingFromInput = document.querySelector('input[name="LiftedFromDate"]')
        const liftingToInput = document.querySelector('input[name="LiftedToDate"]')

        const domShipmentDatesSelected = shipmentFromInput && shipmentToInput &&
          shipmentFromInput.value && shipmentToInput.value
        const domLiftingDatesSelected = liftingFromInput && liftingToInput &&
          liftingFromInput.value && liftingToInput.value

        console.log("DOM values - shipment:", domShipmentDatesSelected, "lifting:", domLiftingDatesSelected)

        // If any of the conditions are met, skip to payment terms
        if (isShipmentMonthSelected || areShipmentDatesSelected || areLiftingDatesSelected ||
          domShipmentDatesSelected || domLiftingDatesSelected) {
          console.log("Condition met - skipping to payment terms")
          const paymentTermsInput = document.querySelector('input[name="Remarks"]')
          if (paymentTermsInput) {
            paymentTermsInput.focus()
            return
          }
        } else {
          console.log("No condition met - normal navigation to shipment from date")
          // Normal navigation to shipment from date
          if (shipmentFromRef.current) {
            shipmentFromRef.current.focus()
            return
          }
        }
      }

      // Handle shipment from date navigation
      if (fieldName === "ShipMentFromDate") {
        if (shipmentToRef.current) {
          shipmentToRef.current.focus()
          return
        }
      }

      // Handle shipment to date navigation
      if (fieldName === "ShipMentToDate") {
        if (remarksRef.current) {
          remarksRef.current.focus()
          return
        }
      }

      // Handle lifting from date navigation
      if (fieldName === "LiftedFromDate") {
        if (liftedToRef.current) {
          liftedToRef.current.focus()
          return
        }
      }

      // Handle lifting to date navigation
      if (fieldName === "LiftedToDate") {
        if (remarksRef.current) {
          remarksRef.current.focus()
          return
        }
      }

      // Normal navigation for other fields
      if (nextRef && nextRef.current) {
        nextRef.current.focus()
      }
    }
  }

  // Handle Page Up and Page Down navigation in dropdowns
  const handleDropdownKeyDown = (e, fieldName) => {
    console.log("handleDropdownKeyDown", e.key, fieldName)
    if (e.key == "PageUp" || e.key == "PageDown") {
      e.preventDefault()

      let options, currentIndex, newIndex, newValue

      if (fieldName === "ContractCombined") {
        // For contract field - use ContractHNo array
        console.log("ContractHNo:", state.ContractHNo)
        const contractOptions = state.ContractHNo.filter(contract => contract.ContractNo && contract.ContractNo.trim() !== "")
        console.log("Contract options available:", contractOptions.length, contractOptions)

        if (contractOptions.length === 0) {
          console.log("No contract options available")
          return // No options to navigate
        }

        // Get current value and find its index
        const currentValue = e.target.value
        currentIndex = -1

        if (currentValue) {
          currentIndex = contractOptions.findIndex(contract => contract.ContractNo === currentValue)
        }

        console.log("Current value:", currentValue, "Current index:", currentIndex)

        if (e.key === "PageUp") {
          // Navigate up (previous option)
          newIndex = currentIndex <= 0 ? contractOptions.length - 1 : currentIndex - 1
        } else {
          // Navigate down (next option)
          newIndex = currentIndex >= contractOptions.length - 1 ? 0 : currentIndex + 1
        }

        newValue = contractOptions[newIndex].ContractNo
        console.log("New value:", newValue, "New index:", newIndex)

        // Update the input value
        e.target.value = newValue

        // Update the form state directly
        setState(prev => ({
          ...prev,
          ContractNo: newValue,
          ContractId: contractOptions[newIndex].Id,
          originalContractNo: newValue,
          contractNumberChanged: true,
          isEditMode: true,
          isEditingEnabled: true,
        }))

        // Load the selected contract data
        const changeEvent = { target: { value: newValue } }
        handleCombinedContractChange(changeEvent)

        // Trigger change event to update the form state
        const changeEvent2 = new Event('change', { bubbles: true })
        e.target.dispatchEvent(changeEvent2)
      } else if (fieldName.startsWith("Note")) {
        // For note fields - improved navigation
        console.log("NotesArray:", state.NotesArray)
        const noteOptions = state.NotesArray.filter(note => note.Name && note.Name.trim() !== "")
        console.log("Note options available:", noteOptions.length, noteOptions)

        if (noteOptions.length === 0) {
          console.log("No note options available")
          return // No options to navigate
        }

        // Get current value and find its index
        const currentValue = e.target.value
        currentIndex = -1

        if (currentValue) {
          currentIndex = noteOptions.findIndex(note => note.Name === currentValue)
        }

        console.log("Current value:", currentValue, "Current index:", currentIndex)

        if (e.key === "PageUp") {
          // Navigate up (previous option)
          newIndex = currentIndex <= 0 ? noteOptions.length - 1 : currentIndex - 1
        } else {
          // Navigate down (next option)
          newIndex = currentIndex >= noteOptions.length - 1 ? 0 : currentIndex + 1
        }

        newValue = noteOptions[newIndex].Name
        console.log("New value:", newValue, "New index:", newIndex)

        // Update the input value
        e.target.value = newValue

        // Update the form state directly
        setState(prev => ({
          ...prev,
          [fieldName]: newValue
        }))

        // Trigger change event to update the form state
        const changeEvent = new Event('change', { bubbles: true })
        e.target.dispatchEvent(changeEvent)
      } else {
        // For regular select dropdowns
        const selectElement = e.target
        options = Array.from(selectElement.options)
        currentIndex = selectElement.selectedIndex

        if (options.length <= 1) return // No options to navigate

        if (e.key === "PageUp") {
          // Navigate up (previous option)
          newIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1
        } else {
          // Navigate down (next option)
          newIndex = currentIndex >= options.length - 1 ? 0 : currentIndex + 1
        }

        // Update the selected value
        newValue = options[newIndex].value
        selectElement.value = newValue

        // Trigger change event to update the form state
        const changeEvent = new Event('change', { bubbles: true })
        e.target.dispatchEvent(changeEvent)

        // Update the form state manually if needed based on field type
        switch (fieldName) {
          case "ContractId":
            const selectedContractById = state.ContractHNo.find(contract => contract.Id == newValue)
            if (selectedContractById) {
              console.log("Selected contract:", selectedContractById)
            }
            break
          case "F_PartyLedger":
            const selectedSeller = state.PartyAccountArray.find(party => party.Id == newValue)
            if (selectedSeller) {
              console.log("Selected seller:", selectedSeller)
            }
            break
          case "F_BrokerLedger":
            const selectedBuyer = state.PartyAccountArray.find(party => party.Id == newValue)
            if (selectedBuyer) {
              console.log("Selected buyer:", selectedBuyer)
            }
            break
          case "F_ItemType":
            const selectedCommodity = state.ItemArray.find(item => item.Id == newValue)
            if (selectedCommodity) {
              console.log("Selected commodity:", selectedCommodity)
            }
            break
          case "F_ContractLedger":
            const selectedContractType = state.TaxAccountArray.find(type => type.Id == newValue)
            if (selectedContractType) {
              console.log("Selected contract type:", selectedContractType)
            }
            break
          case "F_MonthMaster":
            const selectedMonth = state.MonthArray.find(month => month.Id == newValue)
            if (selectedMonth) {
              console.log("Selected month:", selectedMonth)
            }
            break
          case "F_YearMaster":
            const selectedYear = state.YearArray.find(year => year.Id == newValue)
            if (selectedYear) {
              console.log("Selected year:", selectedYear)
            }
            break
          case "LiftingLedger":
            const selectedLiftingLedger = state.PartyAccountArray.find(party => party.Id == newValue)
            if (selectedLiftingLedger) {
              console.log("Selected lifting ledger:", selectedLiftingLedger)
            }
            break
          default:
            console.log(`Field ${fieldName} changed to:`, newValue)
        }
      }
    }
  }

  // Helper function to get display value for form fields
  const getFieldValue = fieldName => {
    if (fieldName === "ContractId") {
      // Return the selected contract ID for dropdown
      return state.ContractId || ""
    }
    if (fieldName === "ContractNo") {
      // When adding new contract (ContractArray is empty), show empty field
      // When editing existing contract, show ContractNo
      if (state.ContractArray && state.ContractArray.length > 0) {
        return state.ContractArray[0]?.ContractNo || ""
      } else {
        return state.ContractNo || ""
      }
    }
    if (fieldName === "ContractCombined") {
      // For combined field, show ContractNo or selected contract
      if (state.ContractArray && state.ContractArray.length > 0) {
        return state.ContractArray[0]?.ContractNo || ""
      } else {
        return state.ContractNo || ""
      }
    }
    return state[fieldName] || ""
  }

  const handleCombinedContractChange = async (e) => {
    const value = e.target.value

    // Store the original contract number before making changes
    const originalContractNo = state.ContractNo || ""

    // Check if this matches an existing contract
    const existingContract = state.ContractHNo.find(contract => contract.ContractNo === value)

    if (existingContract) {
      // Existing contract selected - load it for editing
      setState(prev => ({
        ...prev,
        ContractNo: value,
        ContractId: existingContract.Id,
        originalContractNo: originalContractNo, // Store the original value
        contractNumberChanged: true, // Mark that contract number has been changed
      }))

      // Load the existing contract data
      try {
        await Fn_FillListData(
          dispatch,
          setState,
          "ContractArray",
          `${API_URL8}/Id/${existingContract.Id}`
        )

        // After ContractArray is updated, populate the form
        setTimeout(async () => {
          setState(prev => {
            const contract = prev.ContractArray[0] || {}

            if (contract.Id) {
              // Load contact person data for seller and buyer
              if (contract.F_PartyLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray1",
                  `${API_URL6}/Id/${contract.F_PartyLedger}`
                )
              }

              if (contract.F_BrokerLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "BrokerAccountArray2",
                  `${API_URL6}/Id/${contract.F_BrokerLedger}`
                )
              }

              // Fetch lifting data using ContractH ID
              Fn_FillListData(
                dispatch,
                setState,
                "LiftingArray",
                `${API_URL10}/Id/${contract.Id}`
              )

              return {
                ...prev,
                id: contract.Id || 0,
                Date: formatDateForInput(contract.Date) || new Date().toISOString().split("T")[0],
                ContractNo: contract.ContractNo || "",
                BroContNo: contract.BroContNo || "",
                BroContDate: contract.BroContDate || "",
                ContractId: existingContract.Id,
                originalContractNo: contract.ContractNo || "", // Reset to current contract number when fully loaded
                contractNumberChanged: true, // Keep this true since user selected an existing contract
                F_PartyLedger: contract.F_PartyLedger || 0,
                F_BrokerLedger: contract.F_BrokerLedger || 0,
                F_ItemType: contract.F_ItemType || 0,
                Qty: contract.Qty || 0,
                Rate: contract.Rate || 0,
                InvRate: contract.InvRate || contract.Rate || 0,
                F_UnitMaster: contract.F_UnitMaster || 0,
                F_MonthMaster: contract.F_MonthMaster || 0,
                F_YearMaster: contract.F_YearMaster || 0,
                Vessel: contract.Vessel || "",
                DeliveryPort: contract.DeliveryPort || "",
                ShipMentFromDate: contract.ShipMentFromDate || "",
                ShipMentToDate: contract.ShipMentToDate || "",
                LiftedFromDate: contract.LiftedFromDate || "",
                LiftedToDate: contract.LiftedToDate || "",
                F_ContractLedger: contract.F_ContractLedger || 0,
                Remarks: contract.Remarks || "",
                AdvPayment: contract.AdvPayment || "",
                AdvDate: contract.AdvDate || "",
                Note5: contract.Note5 || "",
                Note6: contract.Note6 || "",
                Note1: contract.Note1 || "",
                Note2: contract.Note2 || "",
                Note3: contract.Note3 || "",
                Note4: contract.Note4 || "",
                importDuty: contract.importDuty || 0,
                tariff: contract.tariff || 0,
                exchangeRate: contract.exchangeRate || 0,
                calPerTonDuty: contract.calPerTonDuty || 0,
                isEditMode: true,
                isEditingEnabled: false,
                contractQty: contract.Qty || 0,
                BackSellerCNo: contract.BackSellerCNo || "",
                SellerPerson: contract.SellerPerson || "",
                BuyerPerson: contract.BuyerPerson || "",
                LiftingLedger: contract.LiftingLedger || 0,
                Type: contract.Type || 1
              }
            }
            return prev
          })
        }, 200)
      } catch (error) {
        console.log("Error loading existing contract:", error)
      }
    } else {
      // New contract number - clear existing data and set to add mode
      setState(prev => ({
        ...prev,
        ContractNo: value,
        ContractId: "",
        ContractArray: [],
        LiftingArray: [],
        isEditMode: false,
        originalContractNo: originalContractNo, // Store the original value
        contractNumberChanged: true, // Mark that contract number has been changed
        LiftingLedger: 0,
      }))
    }
  }

  // Function to clear contract number and reset state
  const handleClearContract = () => {
    setState(prev => ({
      ...prev,
      ContractNo: "",
      ContractId: "",
      originalContractNo: "",
      contractNumberChanged: false,
      ContractArray: [],
      LiftingArray: [],
      isEditMode: false,
      isEditingEnabled: false,
      LiftingLedger: 0,
    }))

    // Reset lifting rows
    setLiftingRows(createDefaultLiftingRows(7))

    // Focus on contract number field
    if (contractNoRef.current) {
      contractNoRef.current.focus()
    }
  }

  // Function to revert contract number change in edit mode
  const handleRevertContractNumber = () => {
    if (state.isEditMode && state.originalContractNo) {
      setState(prev => ({
        ...prev,
        ContractNo: prev.originalContractNo,
        contractNumberChanged: false,
      }))

      // Update the input field value
      if (contractNoRef.current) {
        contractNoRef.current.value = state.originalContractNo
      }
    }
  }

  // Simplified function to handle contract number changes
  const handleContractNumberChangeInEditMode = (e) => {
    const value = e.target.value

    // Check if this matches an existing contract
    const existingContract = state.ContractHNo.find(contract => contract.ContractNo === value)

    if (existingContract) {
      // Existing contract found - load it
      setState(prev => ({
        ...prev,
        ContractNo: value,
        ContractId: existingContract.Id,
        originalContractNo: value,
        contractNumberChanged: true,
        isEditMode: true,
        isEditingEnabled: true,
      }))

      // Load the existing contract data
      handleCombinedContractChange(e)
    } else {
      // New contract number - set to add mode
      setState(prev => ({
        ...prev,
        ContractNo: value,
        ContractId: "",
        ContractArray: [],
        LiftingArray: [],
        isEditMode: false,
        contractNumberChanged: true,
        isEditingEnabled: true,
        id: 0,
      }))

      // Reset lifting rows for new contract
      setLiftingRows(createDefaultLiftingRows(7))
    }
  }

  const selectExistingContract = (id, contractNo) => {
    const originalContractNo = state.ContractNo || ""

    setState(prev => ({
      ...prev,
      ContractNo: contractNo,
      ContractId: id,
      originalContractNo: originalContractNo,
      contractNumberChanged: true // Mark that contract number has been changed
    }))

    // Trigger the change handler to load contract data
    const event = { target: { value: contractNo } }
    handleCombinedContractChange(event)
  }

  return (
    <div className="contract-page">
      <div
        style={{
          height: "3.3rem",
        }}
      ></div>
      <div style={{ height: "auto", overflow: "visible" }}>


        <div className="contract-form" style={{ backgroundColor: "#fffacd", padding: "20px", borderRadius: "8px", height: "auto", minHeight: "auto", overflow: "visible" }}>
          <style>{`
            @media (max-width: 768px) {
              .contract-form {
                padding: 0.5rem !important;
                border-radius: 0.5rem !important;
                height: auto !important;
                min-height: auto !important;
                overflow: visible !important;
              }
            }
            @media (max-width: 576px) {
              .contract-form {
                padding: 0.25rem !important;
                height: auto !important;
                overflow: visible !important;
              }
            }
          `}</style>
          <Form>
            <Row className="g-1">
              {/* Combined Left Panel - Contract Details and Conditions */}
              <Col xs={12} md={12} lg={8}>
                <div className="form-section" style={{ position: "relative" }}>
                  <h6
                    className="section-title"
                    style={{ fontSize: "1rem", fontWeight: "bold", }}
                  >
                    <i className="bx bx-file-doc me-2"></i>
                    Contract Details & Conditions{" "}
                    <span
                      style={{
                        color: state.isEditMode ? "#28a745" : "#007bff",
                        fontSize: "1rem",
                        fontWeight: "bold",
                      }}
                    >
                      ({state.isEditMode ? "Edit Mode" : "Add Mode"})
                    </span>
                    <span
                      style={{
                        color: "#dc3545",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        marginLeft: "10px",
                      }}
                    >
                      ( {state.ContractHNo ? state.ContractHNo.length : 0})
                    </span>
                  </h6>

                  {/* Left Column - Contract Details */}
                  <div className="contract-left-column" style={{ width: "50%", float: "left", paddingRight: "15px" }}>
                    <style>{`
                      @media (max-width: 768px) {
                        .contract-left-column {
                          width: 100% !important;
                          float: none !important;
                          padding-right: 0 !important;
                          margin-bottom: 1rem;
                        }
                      }
                    `}</style>
                    {/* Type Row */}
                    <Row className="g-1">
                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label className="form-label-sm mb-1" style={{ fontSize: "0.75rem" }}>
                            Type
                          </Label>
                          <div className="btn-group w-100" role="group">
                            <button
                              type="button"
                              className={`btn btn-sm ${state.Type === 1 ? 'btn-success' : 'btn-outline-success'}`}
                              onClick={() => setState(prev => ({ ...prev, Type: 1 }))}
                              style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                            >
                              BUY
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${state.Type === 2 ? 'btn-danger' : 'btn-outline-danger'}`}
                              onClick={() => setState(prev => ({ ...prev, Type: 2 }))}
                              style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                            >
                              SELL
                            </button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Date, Serial No, Bro Cont No, Bro Cont Date Row */}
                    <Row className="g-1">
                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="date"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Date
                          </Label>
                          <input
                            type="date"
                            name="Date"
                            id="Date"
                            value={getFieldValue("Date")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={dateRef}
                            onKeyDown={e => handleKeyDown(e, contractNoRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="ContractCombined"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Serial No
                            {state.lastContractNo && (
                              <span className="text-muted ms-1" style={{ fontSize: "0.65rem" }}>
                                ({state.lastContractNo})
                              </span>
                            )}
                          </Label>
                          <input
                            name="ContractCombined"
                            id="ContractCombined"
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "ContractCombined")
                              handleKeyDown(e, broContNoRef)
                            }}
                            value={getFieldValue("ContractCombined")}
                            onChange={handleContractNumberChangeInEditMode}
                            type="text"
                            className="form-control form-control-sm py-1"
                            placeholder="Type contract number (PageUp/PageDown to navigate)"
                            ref={contractNoRef}
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="BroContNo"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Bro Cont No
                          </Label>
                          <input
                            name="BroContNo"
                            id="BroContNo"
                            value={getFieldValue("BroContNo")}
                            onChange={handleInputChange}
                            type="text"
                            className="form-control form-control-sm py-1"
                            placeholder="Bro Cont No"
                            ref={broContNoRef}
                            onKeyDown={e => handleKeyDown(e, broContDateRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="BroContDate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Bro Cont Date
                          </Label>
                          <input
                            type="date"
                            name="BroContDate"
                            id="BroContDate"
                            value={getFieldValue("BroContDate")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Bro Cont Date"
                            ref={broContDateRef}
                            onKeyDown={e => handleKeyDown(e, partyRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>
                    </Row>



                    <Row className="g-1">
                      <Col md={7}>
                        <FormGroup className="mb-1">
                          <Label
                            for="F_PartyLedger"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {state.Type === 1 ? 'Seller' : 'Buyer'}
                          </Label>
                          <select
                            name="F_PartyLedger"
                            id="F_PartyLedger"
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_PartyLedger")
                              handleKeyDown(e, brokerRef)
                            }}
                            value={getFieldValue("F_PartyLedger")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={partyRef}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">{state.Type === 1 ? 'Select Seller' : 'Select Buyer'}</option>
                            {state.PartyAccountArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      <Col md={1}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.65rem" }}
                          >
                            &nbsp;
                          </Label>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={toggleSellerModal}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            style={{
                              width: "100%",
                              height: "28px",
                              padding: "0",
                              fontSize: "0.8rem",
                            }}
                          >
                            +
                          </Button>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label
                            for="sellerName"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Name
                          </Label>
                          <input
                            type="text"
                            name="sellerName"
                            id="Person"
                            value={state.PartyAccountArray1?.[0]?.Person || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Name"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={7}>
                        <FormGroup className="mb-1">
                          <Label
                            for="F_BrokerLedger"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Broker
                          </Label>
                          <select
                            name="F_BrokerLedger"
                            id="F_BrokerLedger"
                            value={getFieldValue("F_BrokerLedger")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={brokerRef}
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_BrokerLedger")
                              handleKeyDown(e, commodityRef)
                            }}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">Select Broker</option>
                            {state.BrokerAccountArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>

                      <Col md={1}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.65rem" }}
                          >
                            &nbsp;
                          </Label>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={toggleBrokerModal}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            style={{
                              width: "100%",
                              height: "28px",
                              padding: "0",
                              fontSize: "0.8rem",
                            }}
                          >
                            +
                          </Button>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label
                            for="Person"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Name
                          </Label>
                          <input
                            type="text"
                            name="Person"
                            id="Person"
                            value={state.BrokerAccountArray2?.[0]?.Person || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Name"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label
                            for="Item"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Commodity
                          </Label>
                          <select
                            name="F_ItemType"
                            id="F_ItemType"
                            value={getFieldValue("F_ItemType")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={commodityRef}
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_ItemType")
                              handleKeyDown(e, qtyRef)
                            }}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">Select Commodity</option>
                            {state.ItemArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>

                      <Col md={1}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.65rem" }}
                          >
                            &nbsp;
                          </Label>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={toggleCommodityModal}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            style={{
                              width: "100%",
                              height: "28px",
                              padding: "0",
                              fontSize: "0.8rem",
                            }}
                          >
                            +
                          </Button>
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-1">
                          <Label
                            for="Qty"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Qty
                          </Label>
                          <input
                            type="number"
                            name="Qty"
                            id="Qty"
                            value={getFieldValue("Qty")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0"
                            ref={qtyRef}
                            onKeyDown={e => handleKeyDown(e, unitRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-1">
                          <Label
                            for="F_UnitMaster"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Unit
                          </Label>
                          <select
                            name="F_UnitMaster"
                            id="F_UnitMaster"
                            value={state.F_UnitMaster || ""}
                            onChange={handleInputChange}
                            className="form-select form-control-sm py-1"
                            ref={unitRef}
                            onKeyDown={e => handleKeyDown(e, rateRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">Select Unit</option>
                            {state.UnitArray && state.UnitArray.map((unit) => (
                              <option key={unit.Id} value={unit.Id}>
                                {unit.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="Rate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Rate
                          </Label>
                          <input
                            type="number"
                            name="Rate"
                            id="Rate"
                            value={getFieldValue("Rate")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0.00"
                            ref={rateRef}
                            onKeyDown={e => handleKeyDown(e, importDutyRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="importDuty"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Import Duty
                          </Label>
                          <input
                            type="number"
                            name="importDuty"
                            id="importDuty"
                            value={state.importDuty}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0.00"
                            ref={importDutyRef}
                            onKeyDown={e => handleKeyDown(e, tariffRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-1">
                          <Label
                            for="tariff"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Tariff
                          </Label>
                          <input
                            type="number"
                            name="tariff"
                            id="tariff"
                            value={state.tariff}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0.00"
                            ref={tariffRef}
                            onKeyDown={e => handleKeyDown(e, exchangeRateRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label
                            for="exchangeRate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Exchange $ Rate
                          </Label>
                          <input
                            type="number"
                            name="exchangeRate"
                            id="exchangeRate"
                            value={state.exchangeRate}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0.00"
                            ref={exchangeRateRef}
                            onKeyDown={e => handleKeyDown(e, calPerTonDutyRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            for="calPerTonDuty"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Duty per Ton
                          </Label>
                          <input
                            type="number"
                            name="calPerTonDuty"
                            id="calPerTonDuty"
                            value={state.calPerTonDuty}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="0.00"
                            ref={calPerTonDutyRef}
                            onKeyDown={e => handleKeyDown(e, contractTypeRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup className="mb-1">
                      <Label
                        className="d-flex align-items-center form-label-sm mb-1"
                        style={{ fontSize: "0.75rem" }}
                      >
                        <i className="bx bx-calculator me-2"></i>
                        Invoice Rate:{" "}
                        <span className="text-primary fw-bold ms-2">
                          {state.InvRate || state.Rate || "0.00"}
                        </span>
                      </Label>
                    </FormGroup>
                  </div>
                  {/* Vertical Separator Line */}
                  <div className="contract-vertical-separator"></div>

                  {/* Right Column - Conditions */}
                  <div className="contract-right-column">
                    <Row className="g-2">
                      <Col md={7}>
                        <FormGroup className="mb-1">
                          <Label
                            for="F_ContractLedger"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Contract Type
                          </Label>
                          <select
                            name="F_ContractLedger"
                            id="F_ContractLedger"
                            value={getFieldValue("F_ContractLedger") || 0}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={contractTypeRef}
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_ContractLedger")
                              handleKeyDown(e, shipMonthRef)
                            }}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">Select Contract Type</option>
                            {state.TaxAccountArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      <Col md={1}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.65rem" }}
                          >
                            &nbsp;
                          </Label>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={toggleContractModal}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                            style={{
                              width: "100%",
                              height: "28px",
                              padding: "0",
                              fontSize: "0.8rem",
                            }}
                          >
                            +
                          </Button>
                        </FormGroup>
                      </Col>

                      <Col md={2}>
                        <FormGroup className="mb-1">
                          <Label
                            for="shipMonth"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Shipment Month
                          </Label>
                          <select
                            name="F_MonthMaster"
                            id="F_MonthMaster"
                            value={getFieldValue("F_MonthMaster")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("ShipMentFromDate") &&
                                getFieldValue("ShipMentToDate")) ||
                              (getFieldValue("LiftedFromDate") &&
                                getFieldValue("LiftedToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={shipMonthRef}
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_MonthMaster")
                              handleKeyDown(e, shipYearRef)
                            }}
                          >
                            <option value="">Select Month</option>
                            {state.MonthArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-1">
                          <Label
                            for="shipYear"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Shipment Year
                          </Label>
                          <select
                            name="F_YearMaster"
                            id="F_YearMaster"
                            value={getFieldValue("F_YearMaster")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("ShipMentFromDate") &&
                                getFieldValue("ShipMentToDate")) ||
                              (getFieldValue("LiftedFromDate") &&
                                getFieldValue("LiftedToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={shipYearRef}
                            onKeyDown={e => {
                              handleDropdownKeyDown(e, "F_YearMaster")
                              handleKeyDown(e, vesselRef)
                            }}
                          >
                            <option value="">Select Year</option>
                            {state.YearArray.map(type => (
                              <option key={type.Id} value={type.Id}>
                                {type.Name}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            for="Vessel"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Vessel Name
                          </Label>
                          <input
                            type="text"
                            name="Vessel"
                            id="Vessel"
                            value={getFieldValue("Vessel")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Enter vessel name"
                            ref={vesselRef}
                            onKeyDown={e => handleKeyDown(e, deliveryPortRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            htmlFor="DeliveryPort"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Delivery Port
                          </Label>
                          <input
                            type="text"
                            name="DeliveryPort"
                            id="DeliveryPort"
                            value={getFieldValue("DeliveryPort")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Enter delivery port"
                            ref={deliveryPortRef}
                            onKeyDown={e => handleKeyDown(e, null)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            for="ShipMentFromDate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Shipment From
                          </Label>
                          <input
                            type="date"
                            name="ShipMentFromDate"
                            id="ShipMentFromDate"
                            value={formatDateForInput(
                              getFieldValue("ShipMentFromDate")
                            )}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("F_MonthMaster") || getFieldValue("F_YearMaster")) ||
                              (getFieldValue("LiftedFromDate") &&
                                getFieldValue("LiftedToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={shipmentFromRef}
                            onKeyDown={e => handleKeyDown(e, null)}
                          />
                          {getFieldValue("ShipMentFromDate") && (
                            <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                              {formatDateForDisplay(getFieldValue("ShipMentFromDate"))}
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            for="ShipMentToDate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            To
                          </Label>
                          <input
                            type="date"
                            name="ShipMentToDate"
                            id="ShipMentToDate"
                            value={formatDateForInput(
                              getFieldValue("ShipMentToDate")
                            )}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("F_MonthMaster") || getFieldValue("F_YearMaster")) ||
                              (getFieldValue("LiftedFromDate") &&
                                getFieldValue("LiftedToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={shipmentToRef}
                            onKeyDown={e => handleKeyDown(e, null)}
                          />
                          {getFieldValue("ShipMentToDate") && (
                            <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                              {formatDateForDisplay(getFieldValue("ShipMentToDate"))}
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            for="LiftedFromDate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Lifting From
                          </Label>
                          <input
                            type="date"
                            name="LiftedFromDate"
                            id="LiftedFromDate"
                            value={formatDateForInput(
                              getFieldValue("LiftedFromDate")
                            )}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("F_MonthMaster") || getFieldValue("F_YearMaster")) ||
                              (getFieldValue("ShipMentFromDate") &&
                                getFieldValue("ShipMentToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={liftedFromRef}
                            onKeyDown={e => handleKeyDown(e, null)}
                          />
                          {getFieldValue("LiftedFromDate") && (
                            <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                              {formatDateForDisplay(getFieldValue("LiftedFromDate"))}
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-1">
                          <Label
                            for="LiftedToDate"
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            To
                          </Label>
                          <input
                            type="date"
                            name="LiftedToDate"
                            id="LiftedToDate"
                            value={formatDateForInput(
                              getFieldValue("LiftedToDate")
                            )}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            disabled={
                              (getFieldValue("F_MonthMaster") || getFieldValue("F_YearMaster")) ||
                              (getFieldValue("ShipMentFromDate") &&
                                getFieldValue("ShipMentToDate")) ||
                              (state.isEditMode && !state.isEditingEnabled)
                            }
                            ref={liftedToRef}
                            onKeyDown={e => handleKeyDown(e, null)}
                          />
                          {getFieldValue("LiftedToDate") && (
                            <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                              {formatDateForDisplay(getFieldValue("LiftedToDate"))}
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={5}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Payment Terms
                          </Label>
                          <input
                            type="text"
                            name="Remarks"
                            id="Remarks"
                            value={getFieldValue("Remarks")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Enter payment terms"
                            ref={remarksRef}
                            onKeyDown={e => handleKeyDown(e, advPaymentRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Advance
                          </Label>
                          <input
                            type="text"
                            name="AdvPayment"
                            id="AdvPayment"
                            value={getFieldValue("AdvPayment")}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            placeholder="Amount in pmt"
                            ref={advPaymentRef}
                            onKeyDown={e => handleKeyDown(e, advDateRef)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-1">
                          <Label
                            className="form-label-sm mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Deposit Date
                          </Label>
                          <input
                            type="date"
                            name="AdvDate"
                            id="AdvDate"
                            value={formatDateForInput(getFieldValue("AdvDate"))}
                            onChange={handleInputChange}
                            className="form-control form-control-sm py-1"
                            ref={advDateRef}
                            onKeyDown={e => handleKeyDown(e, note6Ref)}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={4}>
                        <Input
                          type="text"
                          value={state.DiffAmt || "0.00"}
                          readOnly
                          className={`form-control-sm ${parseFloat(state.DiffAmt || 0) > 0 ? 'text-danger fw-bold' : parseFloat(state.DiffAmt || 0) < 0 ? 'text-success fw-bold' : ''}`}
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Clear float and add Notes section below both columns */}
                  <div style={{ clear: "both" }}></div>

                  {/* Notes Section - Full Width */}
                  <div className="notes-section mt-3">
                    <h6
                      className="section-title"
                      style={{ fontSize: "0.675rem", marginBottom: "0.1rem" }}
                    >
                      <i className="bx bx-note me-2"></i>
                      Notes
                    </h6>
                    <Row className="g-0">
                      <Col md={12}>
                        <FormGroup>
                          <input
                            type="text"
                            name="Note6"
                            id="notes6"
                            value={state.Note6 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-0"
                            placeholder="Write a Note  "
                            ref={note6Ref}
                            onKeyDown={e => {
                              handleNoteKeyDown(e, "Note6")
                              handleDropdownKeyDown(e, "Note6")
                              handleKeyDown(e, note1Ref)
                            }}
                            style={{ marginBottom: "1px" }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                          <input
                            type="text"
                            name="Note1"
                            id="notes1"
                            value={state.Note1 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-0"
                            placeholder="Write a Note "
                            ref={note1Ref}
                            onKeyDown={e => {
                              handleNoteKeyDown(e, "Note1")
                              handleDropdownKeyDown(e, "Note1")
                              handleKeyDown(e, note2Ref)
                            }}
                            style={{ marginBottom: "1px" }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                          <input
                            type="text"
                            name="Note2"
                            id="notes2"
                            value={state.Note2 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-0"
                            placeholder="Write a Note  "
                            ref={note2Ref}
                            onKeyDown={e => {
                              handleNoteKeyDown(e, "Note2")
                              handleDropdownKeyDown(e, "Note2")
                              handleKeyDown(e, note3Ref)
                            }}
                            style={{ marginBottom: "1px" }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                          <input
                            type="text"
                            id="notes3"
                            name="Note3"
                            value={state.Note3 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-0"
                            placeholder="Write a Note  "
                            ref={note3Ref}
                            onKeyDown={e => {
                              handleNoteKeyDown(e, "Note3")
                              handleDropdownKeyDown(e, "Note3")
                              handleKeyDown(e, note4Ref)
                            }}
                            style={{ marginBottom: "1px" }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                          <input
                            type="text"
                            name="Note4"
                            id="notes4"
                            value={state.Note4 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-0"
                            placeholder="Write a Note "
                            ref={note4Ref}
                            onKeyDown={e => {
                              handleNoteKeyDown(e, "Note4")
                              handleDropdownKeyDown(e, "Note4")
                              handleKeyDown(e, note5Ref)
                            }}
                            style={{ marginBottom: "1px" }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                          <input
                            type="text"
                            name="Note5"
                            id="notes5"
                            value={state.Note5 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm"
                            placeholder="Write a Note  "
                            ref={note5Ref}
                            onKeyDown={e => {
                              console.log("Enter key pressed", e.key)
                              if (e.key == "Enter") {
                                console.log("Enter key pressed")
                                e.preventDefault()
                                // Transfer focus to Save button
                                if (saveButtonRef.current) {
                                  saveButtonRef.current.focus()
                                }
                              } else {
                                handleNoteKeyDown(e, "Note5")
                                handleDropdownKeyDown(e, "Note5")
                              }
                            }}
                            autoComplete="off"
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>

              {/* Right Panel - Lifting Details and Notes */}
              <Col xs={12} md={12} lg={4}>
                <div className="form-section">
                  <h6
                    className="section-title"
                    style={{ fontSize: "1rem", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <span>
                      <i className="bx bx-truck me-2"></i>
                      Lifting Details
                    </span>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (state.isEditMode && state.isEditingEnabled) {
                          handleFillDoDetails();
                        }
                      }}
                      style={{
                        color: state.isEditMode && state.isEditingEnabled ? "red" : "gray",
                        textDecoration: "none",
                        fontSize: "0.85rem",
                        fontWeight: "normal",
                        cursor: state.isEditMode && state.isEditingEnabled ? "pointer" : "not-allowed",
                        pointerEvents: state.isEditMode && state.isEditingEnabled ? "auto" : "none"
                      }}
                      onMouseOver={(e) => {
                        if (state.isEditMode && state.isEditingEnabled) {
                          e.target.style.textDecoration = "underline"
                        }
                      }}
                      onMouseOut={(e) => {
                        if (state.isEditMode && state.isEditingEnabled) {
                          e.target.style.textDecoration = "none"
                        }
                      }}
                    >
                      Fill DoDetails
                    </a>
                  </h6>

                  <span className="lifting-ledger-select-wrap">
                    <select
                      name="LiftingLedger"
                      id="LiftingLedger"
                      value={getFieldValue("LiftingLedger")}
                      onChange={handleInputChange}
                      onKeyDown={e => handleDropdownKeyDown(e, "LiftingLedger")}
                      className="form-control form-control-sm py-1"
                      style={{ width: "200px", maxWidth: "100%", height: "30px", fontWeight: "bold" }}
                      disabled={state.isEditMode && !state.isEditingEnabled}
                    >
                      <option value="">Select Lifting</option>
                      {state.PartyAccountArray.map(type => (
                        <option key={type.Id} value={type.Id}>
                          {type.Name}
                        </option>
                      ))}
                    </select>
                  </span>


                  {/* Recreated Responsive Lifting Table */}
                  <div className="lifting-table-container mb-2">
                    <style>{`
                      .lifting-custom-table input[type="date"],
                      .lifting-date-input {
                        color: #000000 !important;
                        background-color: #ffffff !important;
                      }
                      .lifting-custom-table input[type="date"]::-webkit-calendar-picker-indicator,
                      .lifting-date-input::-webkit-calendar-picker-indicator {
                        filter: invert(0) !important;
                        opacity: 1 !important;
                        cursor: pointer !important;
                      }
                      .lifting-custom-table input[type="date"]::-webkit-datetime-edit-text,
                      .lifting-custom-table input[type="date"]::-webkit-datetime-edit-month-field,
                      .lifting-custom-table input[type="date"]::-webkit-datetime-edit-day-field,
                      .lifting-custom-table input[type="date"]::-webkit-datetime-edit-year-field,
                      .lifting-date-input::-webkit-datetime-edit-text,
                      .lifting-date-input::-webkit-datetime-edit-month-field,
                      .lifting-date-input::-webkit-datetime-edit-day-field,
                      .lifting-date-input::-webkit-datetime-edit-year-field {
                        color: #000000 !important;
                        padding: 0 2px !important;
                      }
                      .lifting-date-wrapper {
                        position: relative;
                      }
                      .lifting-date-hint {
                        display: none;
                      }
                      @media (max-width: 768px) {
                        .lifting-date-hint {
                          display: block !important;
                        }
                        .lifting-date-input:invalid:not(:focus) {
                          color: transparent !important;
                        }
                      }
                    `}</style>
                    <div className="lifting-table-responsive">
                      <table className="lifting-custom-table">
                        <thead>
                          <tr>
                            <th style={{ width: '22%' }}>Date</th>
                            <th style={{ width: '22%' }}>Lorry</th>
                            <th style={{ width: '20%' }}>B.No</th>
                            <th style={{ width: '15%' }}>Qty</th>
                            <th style={{ width: '15%' }}>Rate</th>
                            <th style={{ width: '6%', textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {liftingRows.map((row, index) => (
                            <tr key={index}>
                              <td>
                                <div className="lifting-date-wrapper" style={{ position: "relative" }}>
                                  <input
                                    type="date"
                                    name={`Date1-${index}`}
                                    className="form-control form-control-sm lifting-date-input"
                                    value={row.Date1 || ""}
                                    onChange={e => handleLiftingChange(index, "Date1", e.target.value)}
                                    onKeyDown={e => handleLiftingKeyDown(e, index, "Date1")}
                                    placeholder="DD-MM-YYYY"
                                    data-placeholder="DD-MM-YYYY"
                                    disabled={state.isEditMode && !state.isEditingEnabled}
                                    style={{
                                      color: "#000000",
                                      backgroundColor: "#ffffff",
                                      position: "relative"
                                    }}
                                  />
                                  {!row.Date1 && (
                                    <span
                                      className="lifting-date-hint d-md-none"
                                      style={{
                                        position: "absolute",
                                        left: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#6c757d",
                                        fontSize: "0.7rem",
                                        pointerEvents: "none",
                                        zIndex: 1
                                      }}
                                    >
                                      DD-MM-YYYY
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  name={`LorryNo-${index}`}
                                  className="form-control form-control-sm"
                                  value={row.LorryNo || ""}
                                  onChange={e => handleLiftingChange(index, "LorryNo", e.target.value)}
                                  onKeyDown={e => handleLiftingKeyDown(e, index, "LorryNo")}
                                  placeholder="Lorry #"
                                  disabled={state.isEditMode && !state.isEditingEnabled}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  name={`BNo-${index}`}
                                  className="form-control form-control-sm"
                                  value={row.BNo || ""}
                                  onChange={e => handleLiftingChange(index, "BNo", e.target.value)}
                                  onKeyDown={e => handleLiftingKeyDown(e, index, "BNo")}
                                  placeholder="B.No"
                                  disabled={state.isEditMode && !state.isEditingEnabled}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  name={`Lifted-${index}`}
                                  className="form-control form-control-sm"
                                  value={row.Lifted || ""}
                                  onChange={e => handleLiftingChange(index, "Lifted", e.target.value)}
                                  onKeyDown={e => handleLiftingKeyDown(e, index, "Lifted")}
                                  placeholder="Qty"
                                  disabled={state.isEditMode && !state.isEditingEnabled}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  name={`Rate1-${index}`}
                                  className="form-control form-control-sm"
                                  value={row.Rate1 || ""}
                                  onChange={e => handleLiftingChange(index, "Rate1", e.target.value)}
                                  onKeyDown={e => handleLiftingKeyDown(e, index, "Rate1")}
                                  placeholder="Rate"
                                  disabled={state.isEditMode && !state.isEditingEnabled}
                                />
                              </td>
                              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <div className="btn-group btn-group-sm d-flex justify-content-center">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleRemoveLiftingRow(index)}
                                    disabled={(liftingRows && liftingRows.length <= 7) || (state.isEditMode && !state.isEditingEnabled)}
                                    title="Remove row"
                                  >
                                    <i className="bx bx-minus"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm"
                                    onClick={handleAddLiftingRow}
                                    disabled={state.isEditMode && !state.isEditingEnabled}
                                    title="Add new row"
                                  >
                                    <i className="bx bx-plus"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Row Count Info */}
                  <div
                    className="text-muted text-center mb-1"
                    style={{ fontSize: "0.6rem" }}
                  >
                    Total Rows: {liftingRows.length}
                    {liftingRows.length > 7 && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ fontSize: "0.5rem" }}
                      >
                        Scrollable
                      </span>
                    )}
                  </div>

                  {/* Compact Lifting Summary */}
                  <Row className="g-1 mb-1">
                    <Col xs={6} md={3}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Contract Qty
                        </Label>
                        <div
                          className={`form-control-sm py-1 ${state.totalLiftingQty > state.contractQty
                            ? "bg-warning"
                            : "bg-light"
                            }`}
                        >
                          {state.contractQty || state.Qty || "0.00"}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col xs={6} md={3}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Total Lifting
                        </Label>
                        <div
                          className={`form-control-sm py-1 ${state.totalLiftingQty > state.contractQty
                            ? "bg-warning"
                            : "bg-light"
                            }`}
                        >
                          {state.totalLiftingQty || "0.00"}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col xs={6} md={3}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Pending
                        </Label>
                        <div className="form-control-sm py-1 bg-light">
                          {Math.max(
                            0,
                            (state.contractQty || state.Qty || 0) -
                            (state.totalLiftingQty || 0)
                          ).toFixed(2)}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col xs={6} md={3}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Diff. Amt
                        </Label>
                        <input
                          type="text"
                          name="DiffAmt"
                          value={state.DiffAmt || "0.00"}
                          onChange={handleInputChange}
                          className={`form-control-sm py-1 ${parseFloat(state.DiffAmt || 0) < 0
                            ? "text-success"
                            : parseFloat(state.DiffAmt || 0) > 0
                              ? "text-danger"
                              : ""
                            }`}
                          placeholder="0.00"
                          readOnly
                          disabled={state.isEditMode && !state.isEditingEnabled}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Back Seller Contract No */}
                  <Row className="g-1 mb-1">
                    <Col md={12}>
                      <FormGroup>
                        <Label
                          for="BackSellerCNo"
                          className="form-label-sm"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Back Seller CNo
                        </Label>
                        <input
                          type="text"
                          name="BackSellerCNo"
                          id="BackSellerCNo"
                          value={state.BackSellerCNo || ""}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Back seller contract no"
                          disabled={state.isEditMode && !state.isEditingEnabled}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            {/* Action Buttons - Single Row */}
            <Row className="mt-2">
              <Col xs={12}>
                <div className="action-buttons">
                  <style>{`
                    @media (max-width: 768px) {
                      .action-buttons .d-flex {
                        display: grid !important;
                        grid-template-columns: repeat(5, 1fr) !important;
                        gap: 0.4rem !important;
                        width: 100% !important;
                      }
                      .action-buttons .action-btn {
                        width: 100% !important;
                        margin-right: 0 !important;
                        margin-bottom: 0 !important;
                        font-size: 0.7rem !important;
                        padding: 0.35rem 0.25rem !important;
                        min-height: 38px !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                      }
                      .action-buttons .action-btn i {
                        font-size: 0.9rem !important;
                      }
                      .action-buttons .action-btn span {
                        font-size: 0.65rem !important;
                        white-space: nowrap;
                      }
                    }
                  `}</style>
                  <div className="d-flex flex-wrap justify-content-start align-items-start">
                    <button
                      type="button"
                      className="btn btn-warning btn-sm action-btn"
                      onClick={handleEnableEditing}
                    >
                      <i className="bx bx-edit"></i>
                      <span className="d-none d-sm-inline ms-1">Edit</span>
                      <span className="d-inline d-sm-none ms-1">Edit</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm action-btn"
                      onClick={handleUpdateExistingContract}
                      disabled={state.isProgress || (state.isEditMode && !state.isEditingEnabled)}
                      ref={saveButtonRef}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          if (window.confirm("Are you sure you want to save?")) {
                            handleUpdateExistingContract()
                          }
                        }
                      }}
                      style={{
                        transition: "all 0.3s ease",
                        boxShadow: "none"
                      }}
                      onFocus={e => {
                        e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 123, 255, 0.5), 0 0 15px rgba(0, 123, 255, 0.6)"
                        e.target.style.transform = "scale(1.05)"
                      }}
                      onBlur={e => {
                        e.target.style.boxShadow = "none"
                        e.target.style.transform = "scale(1)"
                      }}
                    >
                      {state.isProgress ? (
                        <>
                          <i className="bx bx-loader-alt bx-spin"></i>
                          <span className="ms-1">{state.isEditMode ? "Update" : "Save"}</span>
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save"></i>
                          <span className="ms-1">{state.isEditMode ? "Update" : "Save"}</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm action-btn"
                      onClick={handleDelete}
                      disabled={state.isEditMode && !state.isEditingEnabled}
                    >
                      <i className="bx bx-trash"></i>
                      <span className="ms-1">Delete</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-info btn-sm action-btn"
                      onClick={handlePrint}
                    >
                      <i className="bx bx-printer"></i>
                      <span className="ms-1">Print</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-dark btn-sm action-btn"
                      onClick={handleExit}
                    >
                      <i className="bx bx-exit"></i>
                      <span className="ms-1">Exit</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm action-btn"
                      onClick={handlePDFExport}
                    >
                      <i className="bx bxs-file-pdf"></i>
                      <span className="d-none d-md-inline ms-1">PDF</span>
                      <span className="d-inline d-md-none ms-1">PDF</span>
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      {/* Seller Modal */}
      <Modal isOpen={sellerModal} toggle={toggleSellerModal} size="md" centered>
        <ModalHeader toggle={toggleSellerModal}>
          <h5 className="modal-title">Add New Seller</h5>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newPartyName" className="form-label-sm">
              Ledger Name *
            </Label>
            <Input
              type="text"
              id="newPartyName"
              value={newPartyData.Name}
              onChange={e => handleModalInputChange("Name", e.target.value)}
              placeholder="Enter ledger name"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newPartyPerson" className="form-label-sm">
              Contact Person *
            </Label>
            <Input
              type="text"
              id="newPartyPerson"
              value={newPartyData.Person}
              onChange={e => handleModalInputChange("Person", e.target.value)}
              placeholder="Enter contact person name"
              className="form-control-sm"
            />
          </FormGroup>

          <FormGroup>
            <Label for="newPartyAddress" className="form-label-sm">
              Address *
            </Label>
            <Input
              type="text"
              id="newPartyAddress"
              value={newPartyData.Address}
              onChange={e => handleModalInputChange("Address", e.target.value)}
              placeholder="Enter address"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newPartyGSTNo" className="form-label-sm">
              GST No *
            </Label>
            <Input
              type="text"
              id="newPartyGSTNo"
              value={newPartyData.GSTNo}
              onChange={e => handleModalInputChange("GSTNo", e.target.value)}
              placeholder="Enter GST No"
              className="form-control-sm"
            />
          </FormGroup>

        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleSellerModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => handleSaveNewParty("seller")}>
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Broker Modal */}
      <Modal isOpen={brokerModal} toggle={toggleBrokerModal} size="md" centered>
        <ModalHeader toggle={toggleBrokerModal}>
          <h5 className="modal-title">Add New Broker</h5>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newBrokerName" className="form-label-sm">
              Ledger Name *
            </Label>
            <Input
              type="text"
              id="newBrokerName"
              value={newBrokerData.Name}
              onChange={e => handleBrokerModalInputChange("Name", e.target.value)}
              placeholder="Enter ledger name"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newBrokerPerson" className="form-label-sm">
              Contact Person *
            </Label>
            <Input
              type="text"
              id="newBrokerPerson"
              value={newBrokerData.Person}
              onChange={e => handleBrokerModalInputChange("Person", e.target.value)}
              placeholder="Enter contact person name"
              className="form-control-sm"
            />
          </FormGroup>

          <FormGroup>
            <Label for="newBrokerAddress" className="form-label-sm">
              Address *
            </Label>
            <Input
              type="text"
              id="newBrokerAddress"
              value={newBrokerData.Address}
              onChange={e => handleBrokerModalInputChange("Address", e.target.value)}
              placeholder="Enter address"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newBrokerGSTNo" className="form-label-sm">
              GST No *
            </Label>
            <Input
              type="text"
              id="newBrokerGSTNo"
              value={newBrokerData.GSTNo}
              onChange={e => handleBrokerModalInputChange("GSTNo", e.target.value)}
              placeholder="Enter GST No"
              className="form-control-sm"
            />
          </FormGroup>

        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleBrokerModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveNewBroker}>
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Seller Modal */}
      <Modal
        isOpen={contractModal}
        toggle={toggleContractModal}
        size="md"
        centered
      >
        <ModalHeader toggle={toggleContractModal}>
          <h5 className="modal-title">Add New Contract</h5>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newContractName" className="form-label-sm">
              Contract Type *
            </Label>
            <Input
              type="text"
              id="newContractName"
              value={newContractData.Name}
              onChange={e =>
                handleContractModalInputChange("Name", e.target.value)
              }
              placeholder="Enter contract type"
              className="form-control-sm"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleContractModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => handleSaveNewContract("contract")}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>


      {/* Commodity Modal */}
      <Modal
        isOpen={commodityModal}
        toggle={toggleCommodityModal}
        size="md"
        centered
      >
        <ModalHeader toggle={toggleCommodityModal}>
          <h5 className="modal-title">Add New Commodity</h5>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newCommodityName" className="form-label-sm">
              Commodity Type *
            </Label>
            <Input
              type="text"
              id="newCommodityName"
              value={newCommodityData.Name}
              onChange={e =>
                handleCommodityModalInputChange("Name", e.target.value)
              }
              placeholder="Enter commodity type"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newCommodityAlias" className="form-label-sm">
              Alias
            </Label>
            <Input
              type="text"
              id="newCommodityAlias"
              value={newCommodityData.Alias}
              onChange={e =>
                handleCommodityModalInputChange("Alias", e.target.value)
              }
              placeholder="Enter alias"
              className="form-control-sm"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCommodityModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => handleSaveNewCommodity("commodity")}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* DoDetails Modal */}
      <Modal
        isOpen={doDetailsModal}
        toggle={() => setDoDetailsModal(false)}
        size="xl"
        centered
        style={{ zIndex: 10500 }}
        backdropClassName="bg-dark"
        modalClassName="modal-dialog-centered"
        zIndex={10500}
      >
        <ModalHeader toggle={() => setDoDetailsModal(false)}>
          <h5 className="modal-title">Contract DoDetails</h5>
        </ModalHeader>
        <ModalBody>
          {/* Contract Details Header */}
          <div
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#333",
              textAlign: "center",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "5px",
              marginBottom: "20px"
            }}
          >
            <span style={{ color: "#007bff" }}>ID: {state.DoDetails.Id}</span> |
            <span style={{ color: "#28a745", marginLeft: "10px" }}>Contract: {state.DoDetails.ContractNo}</span> |
            <span style={{ color: "#dc3545", marginLeft: "10px" }}>Date: {state.DoDetails.Date}</span> |
            <span style={{ color: "#6f42c1", marginLeft: "10px" }}>{state.Type === 1 ? 'Seller' : 'Buyer'}: {state.DoDetails.Party}</span> |
            <span style={{ color: "#fd7e14", marginLeft: "10px" }}>Broker: {state.DoDetails.Broker}</span> |
            <span style={{ color: "#198754", marginLeft: "10px" }}>Type: {state.DoDetails.ContractType}</span> |
            <span style={{ color: "#20c997", marginLeft: "10px" }}>Commodity: {state.DoDetails.Commodity}</span> |
            <span style={{ color: "#e83e8c", marginLeft: "10px" }}>Qty: {state.DoDetails.Quantity}</span> |
            <span style={{ color: "#6c757d", marginLeft: "10px" }}>Unit: {state.DoDetails.Unit}</span> |
            <span style={{ color: "#17a2b8", marginLeft: "10px" }}>Vessel: {state.DoDetails.Vessel}</span> |
            <span style={{ color: "#ffc107", marginLeft: "10px" }}>Port: {state.DoDetails.DeliveryPort}</span>
          </div>

          {/* Quantity Summary */}
          {(() => {
            const contractQty = parseFloat(state.DoDetails.Quantity || state.Qty || 0);
            let totalUsedQty = 0;
            state.DoDetailsArray.forEach((row) => {
              totalUsedQty += parseFloat(row.Qty || 0);
            });
            const remainingQty = contractQty - totalUsedQty;
            const isExceeded = totalUsedQty > contractQty;

            return (
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px",
                  backgroundColor: isExceeded ? "#f8d7da" : "#d1ecf1",
                  border: `1px solid ${isExceeded ? "#f5c6cb" : "#bee5eb"}`,
                  borderRadius: "5px",
                  marginBottom: "15px",
                  textAlign: "center"
                }}
              >
                <span style={{ color: "#0c5460", marginRight: "20px" }}>
                  Contract Quantity: <strong>{contractQty}</strong> {state.DoDetails.Unit || ""}
                </span>
                <span style={{ color: "#856404", marginRight: "20px" }}>
                  Total Used: <strong>{totalUsedQty.toFixed(2)}</strong> {state.DoDetails.Unit || ""}
                </span>
                <span style={{ color: isExceeded ? "#dc3545" : "#155724" }}>
                  Remaining: <strong>{remainingQty.toFixed(2)}</strong> {state.DoDetails.Unit || ""}
                </span>
                {isExceeded && (
                  <span style={{ color: "#dc3545", marginLeft: "20px", fontSize: "12px" }}>
                    ⚠️ Exceeded by {Math.abs(remainingQty).toFixed(2)}!
                  </span>
                )}
              </div>
            );
          })()}

          {/* DoDetails Table */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <h6 style={{ color: "#333", margin: 0 }}>DO Details Entry</h6>
              <Button
                color="success"
                size="sm"
                onClick={handleAddNewDoDetailsRow}
                disabled={editingRowIndex !== null}
                title="Add New Row"
              >
                <i className="bx bx-plus" style={{ fontSize: "18px", marginRight: "5px" }}></i>
                Add Row
              </Button>
            </div>
            <Table striped bordered hover responsive size="sm">
              <thead style={{ backgroundColor: "#e9ecef" }}>
                <tr>
                  <th style={{ minWidth: "120px" }}>DO No</th>
                  <th style={{ minWidth: "120px" }}>DO Date</th>
                  <th style={{ minWidth: "100px" }}>Quantity</th>
                  <th style={{ minWidth: "100px" }}>Tank No</th>
                  <th style={{ minWidth: "150px" }}>Transporter Name</th>
                  <th style={{ minWidth: "150px" }}>TERMINAL NAME</th>
                  <th style={{ minWidth: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.DoDetailsArray.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        type="text"
                        value={row.DoNo || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "DoNo", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "100px" }}
                        ref={index === 0 ? doNoInputRef : null}
                      />
                    </td>
                    <td>
                      <Input
                        type="date"
                        value={convertApiDateToInputFormat(row.DoDate) || ""}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          handleDoDetailsInputChange(index, "DoDate", selectedDate);
                        }}
                        disabled={false}
                        size="sm"
                        style={{ minWidth: "100px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={row.Qty || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "Qty", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "80px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.TankNo || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "TankNo", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "80px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.TransName || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "TransName", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "130px" }}
                        placeholder="Enter Transporter Name"
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.LogisticsFirm || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "LogisticsFirm", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "130px" }}
                        placeholder="Enter Logistics Firm"
                      />
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {row.Id ? (
                          // Existing row with ID
                          editingRowIndex === index ? (
                            <>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleSaveUpdateDoDetails(index)}
                              >
                                Update
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleEditDoDetails(index)}
                                disabled={editingRowIndex !== null}
                              >
                                Edit
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handlePDFDoDetails(index)}
                                disabled={editingRowIndex !== null}
                                title="PDF DO Details"
                              >
                                <i className="bx bxs-file-pdf"></i>
                              </Button>
                            </>
                          )
                        ) : (
                          // New row without ID
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleSaveUpdateDoDetails(index)}
                          >
                            Save
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setDoDetailsModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Share PDF Modal */}
      <Modal isOpen={state.showSharePDFModal} toggle={() => setState(prev => ({ ...prev, showSharePDFModal: false }))} className="modal-sm" centered style={{ zIndex: 10600 }} backdropClassName="bg-dark" modalClassName="modal-dialog-centered" zIndex={10600}>
        <ModalHeader toggle={() => setState(prev => ({ ...prev, showSharePDFModal: false }))} className="bg-primary text-white pb-2 pt-2 border-bottom-0">
          <h5 className="modal-title text-white">Share PDF</h5>
        </ModalHeader>
        <ModalBody className="text-center pt-4 pb-4">
          <div className="mb-3">
            <i className="bx bxs-file-pdf text-danger" style={{ fontSize: '48px' }}></i>
          </div>
          <h6>PDF is ready to share!</h6>
          <p className="text-muted small mb-0">You can share it via WhatsApp, Email, or save it to your device.</p>
        </ModalBody>
        <ModalFooter className="border-top-0 d-flex justify-content-center pb-4">
          <Button color="light" onClick={() => setState(prev => ({ ...prev, showSharePDFModal: false }))} className="px-4">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSharePDFClick} className="px-4">
            <i className="bx bx-share-alt me-1"></i> Share Now
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Contract
